// Implementación del backend sobre Supabase.
// Mantiene las MISMAS firmas y shapes que mockApi para que los componentes
// no cambien. Toda la seguridad vive en RLS (ver supabase/migrations/).
//
// IMPORTANTE: las peticiones de datos NO usan supabase.from() — usan fetch
// directo con el token del usuario adjuntado a mano (ver restGet/restWrite).
// supabase-js no siempre adjunta el JWT a las peticiones de datos; esto lo
// garantiza, para que RLS vea auth.uid().

import toast from 'react-hot-toast';
import {
  supabase, getBusinessId, setBusinessId,
  SUPABASE_URL, SUPABASE_ANON_KEY,
} from './supabaseClient';
import { restGet, restWrite, one, authHeaders } from './supabaseRest';
import {
  encolarVenta, sincronizar as _sincronizarCola,
  ventasPendientes, contarPendientes,
} from './colaOffline';
import { inventarioService } from './inventarioService';
import { sanearMetodosPago } from '../config/metodosPago';
import { sanearDescuentosConfig } from '../config/descuentos';

// ─── Helpers internos ────────────────────────────────────────────────────────

function bid() {
  const id = getBusinessId();
  if (!id) throw new Error('no_business');
  return id;
}

// ─── Caché offline (Fase 1: lectura) ─────────────────────────────────────────
// El POS debe seguir mostrando productos, categorías y mesas aunque se caiga
// el internet (cafetería con wifi inestable). Cada lectura exitosa se guarda en
// localStorage; si una lectura falla por RED (no por permisos/datos), se
// devuelve la última copia cacheada en lugar de reventar la pantalla.
//
// Solo cacheamos lecturas idempotentes de catálogo. Las ESCRITURAS (ventas,
// cambios de mesa) NO se encolan en Fase 1 — eso será Fase 2 (cola con reintento).

function _cacheKey(nombre) {
  return `mezo_off_${nombre}_${getBusinessId() || 'anon'}`;
}

function _guardarCache(nombre, data) {
  try {
    localStorage.setItem(_cacheKey(nombre), JSON.stringify({ t: Date.now(), data }));
  } catch { /* cuota llena / modo privado — ignorar */ }
}

function _leerCache(nombre) {
  try {
    const raw = localStorage.getItem(_cacheKey(nombre));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data ?? null;
  } catch {
    return null;
  }
}

// ¿El error vino de que no hay internet, y no de un 401/403/422? Solo en ese
// caso conviene servir caché — devolver datos viejos ante un error de permisos
// sería peor que fallar. fetch() lanza TypeError "Failed to fetch" sin red.
function _esErrorDeRed(e) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const m = (e?.message || '').toLowerCase();
  return m.includes('failed to fetch') || m.includes('networkerror')
      || m.includes('network request failed') || m.includes('load failed');
}

// Envuelve una lectura: intenta la red y cachea el resultado; si la red falla
// devuelve la última copia local (o relanza si no era error de red / no hay caché).
async function _conCache(nombre, fetcher) {
  try {
    const data = await fetcher();
    _guardarCache(nombre, data);
    return data;
  } catch (e) {
    if (_esErrorDeRed(e)) {
      const cache = _leerCache(nombre);
      if (cache != null) return cache;
    }
    throw e;
  }
}

// ─── Feature gating ──────────────────────────────────────────────────────────
// Antes de un POST que crece el negocio (producto, mesa, empleado, categoría)
// chequeamos contra el plan actual. Si superas el límite, tiramos un Error
// específico que el frontend convierte en modal de upgrade.
//
// El frontend ya tiene usePlan() para chequear antes, pero esto es la
// segunda línea: si alguien bypasea el frontend (mock, terminal), el backend
// (vía estas funciones) también bloquea.

const PLAN_LIMITS = {
  semilla: { mesas: 5,        productos: 30,       empleados: 1,        categorias: 10       },
  pro:     { mesas: Infinity, productos: Infinity, empleados: 5,        categorias: Infinity },
  elite:   { mesas: Infinity, productos: Infinity, empleados: Infinity, categorias: Infinity },
};

async function _planActual() {
  try {
    const row = one(await restGet(
      `/businesses?id=eq.${bid()}&select=plan_id,demo_expires_at,plan_expires_at,plan_periodo&limit=1`
    ));
    if (!row) return 'semilla';
    const demoVigente = row.demo_expires_at && new Date() < new Date(row.demo_expires_at);
    if (demoVigente) return 'pro';
    return row.plan_id ?? 'semilla';
  } catch {
    return 'semilla';
  }
}

async function _verificarLimite(recurso, conteoActual) {
  const plan = await _planActual();
  const max  = PLAN_LIMITS[plan]?.[recurso] ?? Infinity;
  if (max === Infinity) return;
  if (conteoActual >= max) {
    const err = new Error(`LIMITE_PLAN:${recurso}:${max}:${plan}`);
    err.code = 'limite_plan';
    err.recurso = recurso;
    err.limite = max;
    err.plan = plan;
    throw err;
  }
}

async function _contar(tabla) {
  try {
    const rows = await restGet(`/${tabla}?business_id=eq.${bid()}&select=id`);
    return (rows ?? []).length;
  } catch {
    return 0;
  }
}

// El frontend a veces envía CARD (datáfono); el enum del backend es BOLD.
const PAYMENT_MAP = { CARD: 'BOLD' };
const normalizePM = (pm) => PAYMENT_MAP[pm] ?? pm;

// ─── Mappers (DB snake_case ↔ frontend español) ──────────────────────────────

function fromProduct(p) {
  // Mapeo a las columnas REALES de la tabla products:
  // name, price, cost, category_id, descripcion, imagen_url, disponible, sku.
  // (Antes este mapper buscaba p.image / p.image_type / p.available que no
  // existen en la DB — daban undefined silencioso, no rompía pero los
  // campos quedaban siempre con sus defaults).
  return {
    id:          p.id,
    nombre:      p.name,
    precio:      Number(p.price) || 0,
    costo:       Number(p.cost)  || 0,
    categoriaId: p.category_id,
    descripcion: p.descripcion ?? null,
    imagenUrl:   p.imagen_url ?? null,
    // Compat: algunos componentes usan `imagen`, lo mantenemos
    imagen:      p.imagen_url ?? null,
    sku:         p.sku ?? null,
    disponible:  p.disponible !== false,
    // Receta (migración 0040): liga al inventario para descontar al vender +
    // pasos de preparación para la cocina. `select=*` trae estas columnas si
    // existen; si la migración aún no corrió, llegan undefined y caen al default.
    inventarioAsociado: p.receta_inventario ?? { activo: false, items: [] },
    recetaPasos:        Array.isArray(p.receta_pasos) ? p.receta_pasos : [],
    // false = se saca de nevera/estante, no aparece en cocina (0045).
    pasaPorCocina:      p.pasa_por_cocina !== false,
    // Extras/variantes con precio [{nombre, precio}] — el cajero los elige (0047).
    modificadores:      Array.isArray(p.modificadores) ? p.modificadores : [],
    creadoEn:    p.created_at ? new Date(p.created_at) : new Date(),
    businessId:  p.business_id,
  };
}

// Guarda la receta (inventario + pasos) en columnas JSONB APARTE del INSERT
// principal del producto. Es best-effort a propósito: si la migración 0040 aún
// no corre, el PATCH falla con 400 por columna inexistente y lo tragamos — así
// crear/editar productos (camino crítico del onboarding) nunca se rompe.
async function _guardarRecetaProducto(id, payload) {
  if (!id) return;
  const patch = {};
  if (payload.inventarioAsociado !== undefined)
    patch.receta_inventario = payload.inventarioAsociado ?? { activo: false, items: [] };
  if (payload.recetaPasos !== undefined)
    patch.receta_pasos = Array.isArray(payload.recetaPasos) ? payload.recetaPasos : [];
  if (payload.pasaPorCocina !== undefined)
    patch.pasa_por_cocina = payload.pasaPorCocina !== false;   // 0045
  if (payload.modificadores !== undefined)
    patch.modificadores = Array.isArray(payload.modificadores) ? payload.modificadores : []; // 0047
  if (!Object.keys(patch).length) return;

  // Persistencia POR CAPAS: si una migración reciente no ha corrido, se
  // reintenta quitando sus campos (del más nuevo al más viejo) para que lo
  // demás sí se guarde. Nunca arrastrar todo por una columna faltante.
  const capas = [
    patch,
    (() => { const { modificadores, ...r } = patch; return r; })(),                       // sin 0047
    (() => { const { modificadores, pasa_por_cocina, ...r } = patch; return r; })(),     // sin 0047 ni 0045
  ];
  let ultimoError = null;
  for (const capa of capas) {
    if (!Object.keys(capa).length) break;
    try {
      await restWrite('PATCH', `/products?id=eq.${id}&business_id=eq.${bid()}`, capa, 'return=minimal');
      return;
    } catch (e) { ultimoError = e; }
  }
  // eslint-disable-next-line no-console
  console.warn('[mezo] receta no guardada (¿faltan migraciones 0040/0045/0047?):', ultimoError?.message);
}

function toProductRow(p) {
  // IMPORTANTE: solo enviar columnas que EXISTEN en la tabla products.
  // Schema (0001 + 0017): id, business_id, category_id, name, price, cost,
  // sku, imagen_url, disponible, descripcion, created_at.
  //
  // ANTES enviábamos: currency, image_type, image, description, ingredients.
  // Ninguna existe en la DB. Resultado: INSERT/PATCH fallaba con 400 → cliente
  // nuevo veía "Veci, algo salió mal" en el último paso del onboarding y NO
  // podía registrarse. Bug detectado con Kero Café (sebvasxiii@gmail.com).
  //
  // El emoji (🍽️ etc.) NO se persiste — quedaba "guardado" como image_type
  // pero la columna nunca existió. Para no perderlo, lo mapeamos a `imagen_url`
  // solo si es realmente una URL; si es emoji lo ignoramos (lo seleccionamos
  // visualmente en cliente desde el nombre del producto).
  const row = {
    name:         p.nombre,
    price:        Number(p.precio) || 0,
    cost:         Number(p.costo)  || 0,
    category_id:  p.categoriaId ?? null,
    descripcion:  p.descripcion ?? p.description ?? null,
    disponible:   p.disponible !== false,
  };
  if (p.imagenUrl && /^https?:\/\//.test(p.imagenUrl)) {
    row.imagen_url = p.imagenUrl;
  } else if (p.imagenUrl === null) {
    // null EXPLÍCITO = quitar la imagen (el form la borró). undefined = no tocar.
    row.imagen_url = null;
  }
  if (p.sku) row.sku = p.sku;
  return row;
}

function fromCategoria(c) {
  // Schema real de categories: id, business_id, name, orden (no sort_order),
  // created_at. No tiene columna icon — usamos un emoji default generado
  // del nombre o un fallback genérico.
  return {
    id:         c.id,
    nombre:     c.name,
    // El emoji ya no se persiste — se infiere del nombre para retro-compat
    emoji:      c.icon ?? '🍽️',
    orden:      c.orden ?? c.sort_order ?? 0,
    businessId: c.business_id,
    creadoEn:   c.created_at ? new Date(c.created_at) : new Date(),
  };
}

function toCategoriaRow(c) {
  // Solo enviar columnas que existen en categories: name, orden, business_id.
  // El emoji NO se persiste (la columna icon no existe en el schema).
  return { name: c.nombre, orden: c.orden ?? 0 };
}

function fromNegocio(n) {
  if (!n) return null;
  return {
    id:                 n.id,
    name:               n.name,
    type:               n.type,
    phone:              n.phone ?? null,
    address:            n.address,
    city:               n.city,
    country:            n.country,
    openingTime:        n.open_at ?? null,
    closingTime:        n.close_at ?? null,
    isOpen:             n.is_open ?? false,
    tableCount:         n.table_count ?? 0,
    logoUrl:            n.logo_url ?? null,

    // ── Personalización de factura (migración 0008) ────────────────────────
    razonSocial:        n.razon_social ?? null,
    nit:                n.nit ?? null,
    direccionFiscal:    n.direccion_fiscal ?? null,
    ciudad:             n.ciudad ?? null,
    telefono:           n.telefono ?? null,
    email:              n.email_contacto ?? null,
    sector:             n.sector ?? 'restaurante',
    regimenTributario:  n.regimen_tributario ?? 'SIMPLE',
    cobraInc:           n.cobra_inc ?? true,
    incIncluidoPrecio:  n.inc_incluido_precio ?? true,
    cobraIva:           n.cobra_iva ?? false,
    ivaIncluidoPrecio:  n.iva_incluido_precio ?? true,
    resolucionDian:     n.resolucion_dian ?? null,
    pieFactura:         n.pie_factura ?? null,
    propinaSugeridaPct: n.propina_sugerida_pct ?? 10,
    colorPrimario:      n.color_primario ?? null,

    // ── Menú público con QR (migración 0017) ─────────────────────────────────
    menuPublicoActivo:   n.menu_publico_activo ?? false,
    // Autopedido desde el QR + métodos de pago del negocio (migración 0041).
    autopedidoActivo:    n.autopedido_activo ?? false,
    pagoQrConfig:        n.pago_qr_config ?? {},
    menuPublicoMensaje:  n.menu_publico_mensaje ?? 'Bienvenido a nuestro menú',
    menuPublicoColor:    n.menu_publico_color ?? null,
    menuModo:                n.menu_modo ?? 'digital',
    menuPdfUrl:              n.menu_pdf_url ?? null,
    menuQrViewsCount:        n.menu_qr_views_count ?? 0,
    menuTemplate:            n.menu_template ?? 'latte-art',
    menuColorSecundario:     n.menu_color_secundario ?? null,
    menuImagenHeader:        n.menu_imagen_header ?? null,
    menuMostrarLogo:         n.menu_mostrar_logo ?? true,
    menuMostrarDescripciones: n.menu_mostrar_descripciones ?? true,
    menuEscala:              n.menu_escala ?? 'normal',
    menuMostrarFotos:        n.menu_mostrar_fotos ?? true,

    // ── Plan + suscripción (migración 0010) ──────────────────────────────────
    planId:                       n.plan_id ?? 'semilla',
    planPeriodo:                  n.plan_periodo ?? null,
    planActivatedAt:              n.plan_activated_at ?? null,
    planExpiresAt:                n.plan_expires_at ?? null,
    planStatus:                   n.plan_status ?? 'activo',
    demoStartedAt:                n.demo_started_at ?? null,
    demoExpiresAt:                n.demo_expires_at ?? null,
    pagoPendienteVerificacion:    n.pago_pendiente_verificacion ?? false,

    // ── Programa de fidelidad (migración 0013) ──────────────────────────────
    loyaltyActivo:                n.loyalty_activo ?? false,
    loyaltyPuntosPorCop:          Number(n.loyalty_puntos_por_cop ?? 1),
    loyaltyMensajeCanje:          n.loyalty_mensaje_canje ?? null,

    // ── Propina digital en el menú QR (migración 0029) ──────────────────────
    propinaQrActiva:              n.propina_qr_activa ?? false,
    propinaQrNumero:              n.propina_qr_numero ?? null,
    propinaQrTipo:                n.propina_qr_tipo ?? 'nequi',
    propinaQrMensaje:             n.propina_qr_mensaje ?? '¿Te atendimos bien? Deja propina a tu mesero 💛',
    dianFacturacionActiva:        n.dian_facturacion_activa ?? false,
    dianProveedor:                n.dian_proveedor ?? 'factus',
    dianPrefijo:                  n.dian_prefijo ?? null,
    dianRangoDesde:               n.dian_rango_desde ?? null,
    dianRangoHasta:               n.dian_rango_hasta ?? null,
    dianAmbiente:                 n.dian_ambiente ?? 'pruebas',

    // ── Asistencia con evidencia (migración 0050) ───────────────────────────
    // Se PERSISTEN con guardarUbicacionNegocio (PATCH aparte best-effort), no
    // con toNegocioRow: guardar el resto de la config nunca debe romperse por
    // una columna que aún no existe.
    ubicacionLat:                 n.ubicacion_lat ?? null,
    ubicacionLng:                 n.ubicacion_lng ?? null,
    radioAsistenciaM:             n.radio_asistencia_m ?? 150,
    asistenciaRequiereFoto:       n.asistencia_requiere_foto ?? false,
    asistenciaRequiereGps:        n.asistencia_requiere_gps ?? false,
    // Llave del local (0058): el FLAG es publico para los miembros (la UI de
    // Mi turno necesita saber si pedir el codigo); el SECRETO nunca viaja.
    asistenciaRequiereLlave:      n.asistencia_requiere_llave ?? false,
    // Checklist de apertura/cierre (0058). null = nunca configurado.
    checklistConfig:              n.checklist_config ?? null,
    // Medios de pago configurables (0060). null = los 5 de siempre.
    metodosPago:                  n.metodos_pago ?? null,
    // Descuentos con autorización (0067). null = apagado.
    descuentosConfig:             n.descuentos_config ?? null,
  };
}

// Mapper para PATCH /businesses. Filtra propiedades undefined antes de devolverlas:
// si el llamador no envió un campo, NO debe pisarlo a null en la DB.
function toNegocioRow(n) {
  const map = {
    name:                 n.name,
    type:                 n.type,
    phone:                n.phone,
    address:              n.address,
    city:                 n.city,
    country:              n.country,
    open_at:              n.openingTime,
    close_at:             n.closingTime,
    table_count:          n.tableCount != null ? (Number(n.tableCount) || 0) : undefined,

    // Factura colombiana
    razon_social:         n.razonSocial,
    nit:                  n.nit,
    direccion_fiscal:     n.direccionFiscal,
    ciudad:               n.ciudad,
    telefono:             n.telefono,
    email_contacto:       n.email,
    sector:               n.sector,
    regimen_tributario:   n.regimenTributario,
    cobra_inc:            n.cobraInc,
    inc_incluido_precio:  n.incIncluidoPrecio,
    cobra_iva:            n.cobraIva,
    iva_incluido_precio:  n.ivaIncluidoPrecio,
    resolucion_dian:      n.resolucionDian,
    pie_factura:          n.pieFactura,
    propina_sugerida_pct: n.propinaSugeridaPct != null ? Number(n.propinaSugeridaPct) : undefined,
    color_primario:       n.colorPrimario,

    // Plan + suscripción
    plan_id:                      n.planId,
    plan_periodo:                 n.planPeriodo,
    plan_activated_at:            n.planActivatedAt,
    plan_expires_at:              n.planExpiresAt,
    plan_status:                  n.planStatus,
    demo_started_at:              n.demoStartedAt,
    demo_expires_at:              n.demoExpiresAt,
    pago_pendiente_verificacion:  n.pagoPendienteVerificacion,

    // Fidelidad
    loyalty_activo:               n.loyaltyActivo,
    loyalty_puntos_por_cop:       n.loyaltyPuntosPorCop != null ? Number(n.loyaltyPuntosPorCop) : undefined,
    loyalty_mensaje_canje:        n.loyaltyMensajeCanje,

    // Propina digital
    propina_qr_activa:            n.propinaQrActiva,
    propina_qr_numero:            n.propinaQrNumero,
    propina_qr_tipo:              n.propinaQrTipo,
    propina_qr_mensaje:           n.propinaQrMensaje,

    // Menú público
    menu_publico_activo:          n.menuPublicoActivo,
    // OJO: autopedido_activo / pago_qr_config NO van aquí — se persisten en
    // updateNegocio con un PATCH best-effort aparte, para que guardar el menú no
    // se rompa si la migración 0041 aún no corrió (igual que la receta).
    menu_publico_mensaje:         n.menuPublicoMensaje,
    menu_publico_color:           n.menuPublicoColor,
    menu_modo:                    n.menuModo,
    menu_pdf_url:                 n.menuPdfUrl,
    menu_template:                n.menuTemplate,
    menu_color_secundario:        n.menuColorSecundario,
    menu_imagen_header:           n.menuImagenHeader,
    menu_mostrar_logo:            n.menuMostrarLogo,
    menu_mostrar_descripciones:   n.menuMostrarDescripciones,
    menu_escala:                  n.menuEscala,
    menu_mostrar_fotos:           n.menuMostrarFotos,
    dian_facturacion_activa:      n.dianFacturacionActiva,
    dian_prefijo:                 n.dianPrefijo,
    dian_rango_desde:             n.dianRangoDesde,
    dian_rango_hasta:             n.dianRangoHasta,
    dian_ambiente:                n.dianAmbiente,
  };
  // Quitamos undefined para no sobrescribir columnas en la DB con null por accidente.
  return Object.fromEntries(Object.entries(map).filter(([, v]) => v !== undefined));
}

function fromOrderLine(l) {
  return {
    productId: l.product_id,
    name:      l.product_name,
    nombre:    l.product_name,
    unitPrice: l.unit_price ?? 0,
    precio:    l.unit_price ?? 0,
    quantity:  l.quantity ?? 1,
    cantidad:  l.quantity ?? 1,
    subtotal:  l.subtotal ?? (l.unit_price * l.quantity),
    nota:      l.notas ?? null,   // nota de preparación ("sin cebolla") — 0042
    // Descuento por línea con motivo (0062) y quién lo autorizó (0067).
    descuentoMonto:         Number(l.descuento_monto) || 0,
    descuentoMotivo:        l.descuento_motivo ?? null,
    descuentoAutorizadoPor: l.descuento_autorizado_por ?? null,
    descuentoAutorizadoId:  l.descuento_autorizado_id ?? null,
  };
}

// Inserta líneas de orden tolerando que la columna `notas` (migración 0042)
// aún no exista. PostgREST exige llaves uniformes y rechaza columnas
// desconocidas, así que: solo incluimos `notas` si alguna línea trae nota, y
// si el backend la rechaza reintentamos sin notas — una venta NUNCA se
// bloquea por una nota.
async function _insertarLineas(rows) {
  const hayNotas = rows.some((r) => r.notas);
  const payload = hayNotas ? rows : rows.map(({ notas, ...r }) => r);
  try {
    await restWrite('POST', '/order_lines', payload, 'return=minimal');
  } catch (e) {
    if (!hayNotas) throw e;
    // eslint-disable-next-line no-console
    console.warn('[mezo] notas no guardadas (¿falta la migración 0042?):', e?.message);
    await restWrite('POST', '/order_lines', rows.map(({ notas, ...r }) => r), 'return=minimal');
  }
}

function fromOrder(o) {
  if (!o) return null;
  const lines = (o.order_lines ?? o.lines ?? []).map(fromOrderLine);
  const total = o.total ?? lines.reduce((s, l) => s + l.subtotal, 0);
  const tip   = o.tip ?? 0;

  const status = o.status === 'CLOSED' ? 'PAID' : o.status;
  let estadoCocina = null;
  if (['OPEN', 'PREPARING'].includes(o.status)) estadoCocina = 'preparando';
  else if (o.status === 'READY')                estadoCocina = 'listo';

  return {
    id:            o.id,
    status,
    estadoCocina,
    origen:        o.origen ?? 'pos',   // 'qr_cliente' = autopedido del cliente
    cocinaEn:      o.created_at ? new Date(o.created_at) : null,
    items:         lines,
    lineas:        lines,
    subtotal:      lines.reduce((s, l) => s + l.subtotal, 0),
    tip,
    total,
    paymentMethod: o.payment_method ?? null,
    saleNumber:    null,
    tableId:       o.table_id ?? null,
    // Nombre del empleado que tomó/cobró la orden — llega por el embed
    // team_members(name) de getOrdenes (orders.employee_id → team_members).
    employeeName:  o.team_members?.name ?? null,
    employeeId:    o.employee_id ?? null,
    shiftId:       o.shift_id ?? null,
    createdAt:     o.created_at ? new Date(o.created_at) : new Date(),
  };
}

function fromTable(t, activeOrders = []) {
  const order = activeOrders.find((o) => o.table_id === t.id);
  const lines = order
    ? (order.order_lines ?? []).map((l) => ({
        id:        l.id,           // necesario para borrar la línea desde el panel
        orderId:   order.id,
        productId: l.product_id,
        nombre:    l.product_name,
        precio:    l.unit_price,
        cantidad:  l.quantity,
        subtotal:  l.subtotal,
        // Nota de preparación (0042): sin esto el carrito al cobrar la mesa
        // mostraba las líneas enviadas SIN su nota ("sin cebolla" se perdía
        // en el ticket y el recibo, aunque cocina sí la recibió).
        nota:      l.notas ?? null,
        // Descuento con motivo (0062) y quién lo autorizó (0067): sin esto el
        // carrito cobraba la mesa al bruto mientras el servidor guardaba el neto.
        descuentoMonto:         Number(l.descuento_monto) || 0,
        descuentoMotivo:        l.descuento_motivo ?? null,
        descuentoAutorizadoPor: l.descuento_autorizado_por ?? null,
      }))
    : null;

  // Si hay orden activa, el estado se deriva de ella. Si no, se usa el
  // estado guardado en la mesa (lo que permite "Ocupar / Reservar" manual).
  let estado;
  if (order) {
    if (['OPEN', 'PREPARING', 'READY'].includes(order.status)) estado = 'ocupada';
    else if (order.status === 'DELIVERED')                     estado = 'pagando';
    else                                                       estado = 'libre';
  } else {
    estado = t.estado ?? 'libre';
  }

  return {
    id:            t.id,
    numero:        t.number,
    nombre:        `Mesa ${t.number}`,
    estado,
    ocupadaEn:     order
      ? new Date(order.created_at)
      : (t.ocupada_en ? new Date(t.ocupada_en) : null),
    total:         order?.total ?? null,
    personas:      t.personas ?? null,
    lineas:        lines,
    // ID de la orden abierta — lo necesitamos para anexar productos
    // (Tomar pedido / Agregar productos) sin duplicar la orden.
    activeOrderId: order?.id ?? null,
    activeStatus:  order?.status ?? null,
    // Posición en el plano del salón (migración 0043). Null = sin ubicar.
    posX:          t.pos_x ?? null,
    posY:          t.pos_y ?? null,
    // Zona real (0044). Null = sin zona → solo visible en "Todas".
    zonaId:        t.zone_id ?? null,
  };
}

function fromEmpleado(m) {
  const roleMap = { ADMIN: 'admin', CASHIER: 'cajera', WAITER: 'mesero', KITCHEN: 'cocina' };
  return {
    id:        m.id,
    nombre:    m.name ?? m.email?.split('@')[0] ?? '—',
    correo:    m.email,
    // El PIN ya no viaja al navegador (0053: se guarda hasheado y la columna
    // en claro queda con un CHECK que exige null). Lo que la UI necesita saber
    // es si HAY pin, no cuál es: eso llega por `mezo_pin_estado` y se pega en
    // `tienePin`. Antes de 0053 seguimos leyendo el valor viejo para no romper
    // nada mientras la migración no esté corrida.
    pin:       m.pin ?? null,
    tienePin:  m.pin != null,
    roles:     [roleMap[m.role] ?? 'cajera'],
    activo:    m.status === 'ACTIVE',
    estado:    m.status,                // INVITED | ACTIVE | INACTIVE
    avatarUrl: m.avatar_url ?? null,
    // Overrides de permisos por empleado (0049). Antes de la migración la
    // columna no existe → {} (el hook usa los defaults del rol).
    permisos:  (m.permisos && typeof m.permisos === 'object') ? m.permisos : {},
    creadoEn:  m.invited_at ? new Date(m.invited_at) : new Date(),
  };
}

// Sube avatar del empleado al bucket PRIVADO `empleado-avatares` y guarda
// una signed URL de larga duración (1 año) en team_members.avatar_url.
//
// Bucket privado + signed URL evita que las fotos sean enumerables por
// /storage/v1/object/public/... mientras mantiene la simplicidad de
// <img src> en el componente (la URL ya está firmada y dura 1 año).
// Cuando expire, el dueño sube de nuevo la foto.
export async function subirAvatarEmpleado(empleadoId, file) {
  if (!file) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { toast.error('Sesión inactiva.'); return null; }

  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
  const businessId = bid();
  // Random suffix evita que el path sea adivinable si alguien conoce
  // business_id + empleado_id (defensa en profundidad).
  const rnd = Math.random().toString(36).slice(2, 10);
  const path = `${businessId}/${empleadoId}-${rnd}.${ext}`;

  const upRes = await fetch(
    `${SUPABASE_URL}/storage/v1/object/empleado-avatares/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type || 'image/jpeg',
        'x-upsert': 'true',
      },
      body: file,
    },
  );
  if (!upRes.ok) {
    toast.error('No pudimos subir la foto.');
    return null;
  }

  // Generar signed URL con TTL de 1 año (max permitido por Supabase Storage).
  const { data: signed, error: sErr } = await supabase
    .storage
    .from('empleado-avatares')
    .createSignedUrl(path, 60 * 60 * 24 * 365);
  if (sErr || !signed?.signedUrl) {
    toast.error('Foto subida, pero no pudimos generar el enlace.');
    return null;
  }
  const signedUrl = signed.signedUrl;

  await restWrite('PATCH',
    `/team_members?id=eq.${empleadoId}&business_id=eq.${businessId}`,
    { avatar_url: signedUrl }, 'return=minimal');
  return signedUrl;
}

function fromMovimiento(m) {
  return {
    id:          m.id,
    tipo:        m.tipo,
    descripcion: m.descripcion,
    monto:       m.monto,
    metodo:      m.metodo ?? 'CASH',
    creadoEn:    m.creado_en ? new Date(m.creado_en) : new Date(),
  };
}

function fromShift(s) {
  if (!s) return null;
  return {
    id:              s.id,
    empleadoId:      s.empleado_id,
    empleadoNombre:  s.empleado_nombre,
    baseTurno:       s.base_turno ?? 0,
    inicio:          s.inicio ? new Date(s.inicio) : null,
    fin:             s.fin    ? new Date(s.fin)    : null,
    duracionMin:     s.duracion_min ?? null,
    numOrdenes:      s.num_ordenes ?? 0,
    roles:           s.roles ?? ['cajera'],
    nota:            s.nota ?? null,
    // Evidencia de asistencia (0050). Antes de la migración las columnas no
    // vienen → null; la UI pinta "Sin evidencia".
    fotoEntradaPath:   s.foto_entrada_path ?? null,
    fotoSalidaPath:    s.foto_salida_path ?? null,
    latEntrada:        s.lat_entrada ?? null,
    lngEntrada:        s.lng_entrada ?? null,
    latSalida:         s.lat_salida ?? null,
    lngSalida:         s.lng_salida ?? null,
    precisionEntradaM: s.precision_entrada_m != null ? Number(s.precision_entrada_m) : null,
    precisionSalidaM:  s.precision_salida_m  != null ? Number(s.precision_salida_m)  : null,
    distanciaEntradaM: s.distancia_entrada_m != null ? Number(s.distancia_entrada_m) : null,
    distanciaSalidaM:  s.distancia_salida_m  != null ? Number(s.distancia_salida_m)  : null,
    enPuntoEntrada:    typeof s.en_punto_entrada === 'boolean' ? s.en_punto_entrada : null,
    enPuntoSalida:     typeof s.en_punto_salida  === 'boolean' ? s.en_punto_salida  : null,
    dispositivo:       s.dispositivo ?? null,
  };
}

// ─── Auth ────────────────────────────────────────────────────────────────────

function unwrapUser(authUser, claims = {}) {
  return {
    id:            authUser.id,
    email:         authUser.email,
    role:          claims.role ?? authUser.user_metadata?.role ?? null,
    planType:      authUser.user_metadata?.plan ?? 'Pro',
    planExpiresAt: null,
  };
}

// Resuelve el negocio del usuario y lo guarda en localStorage.
// Busca primero por user_id. Si no encuentra (puede haber sido invitado por
// email antes de crear su auth.user, o un edge case de user_id null), cae a
// buscar por email — y si encuentra, repara el user_id de la fila.
async function resolveBusiness(userId, email = null) {
  if (!userId) return null;
  try {
    let row = one(await restGet(
      `/team_members?user_id=eq.${userId}&status=eq.ACTIVE&select=business_id&order=joined_at.asc&limit=1`
    ));

    if (!row?.business_id && email) {
      const emailEnc = encodeURIComponent(email);
      row = one(await restGet(
        `/team_members?email=eq.${emailEnc}&status=eq.ACTIVE&select=business_id&order=joined_at.asc&limit=1`
      ));
      // Si lo encontramos por email, repara el user_id para futuros logins
      if (row?.business_id) {
        await restWrite('PATCH',
          `/team_members?business_id=eq.${row.business_id}&email=eq.${emailEnc}`,
          { user_id: userId }, 'return=minimal'
        ).catch(() => {});
      }
    }

    if (!row?.business_id) return null;
    setBusinessId(row.business_id);
    return row.business_id;
  } catch {
    return null;
  }
}

// Rol + overrides de permisos del usuario en el negocio actual (RPC
// get_mis_permisos, migración 0049). Si la RPC aún no existe (pre-0049) o
// falla, devolvemos vacío y usePermisos aplica los defaults del rol — nunca
// bloqueamos la app por esto.
export async function getMisPermisos() {
  const vacio = { rol: null, overrides: {}, permisos: null, permisosRoles: null };
  const id = getBusinessId();
  if (!id) return vacio;
  try {
    const { data, error } = await supabase.rpc('get_mis_permisos', { p_business_id: id });
    if (error) {
      const noExiste = /does not exist|not find|404|PGRST202/i.test(error.message || '');
      if (!noExiste) console.warn('[mezo] get_mis_permisos falló:', error.message);
      return vacio;
    }
    return {
      rol:       data?.role ?? null,
      overrides: (data?.overrides && typeof data.overrides === 'object') ? data.overrides : {},
      permisos:  (data?.permisos  && typeof data.permisos  === 'object') ? data.permisos  : null,
      // Capa 2 (0052): mapa por rol del negocio. Si la RPC es la de 0049 no
      // viene → null, y usePermisos lo pide aparte con getPermisosRoles().
      permisosRoles: (data?.permisos_roles && typeof data.permisos_roles === 'object') ? data.permisos_roles : null,
    };
  } catch {
    return vacio;
  }
}

// Overrides de permisos POR ROL del negocio (businesses.permisos_roles, 0052):
// { CASHIER: { 'pos.anular_linea': true } }. Cualquier miembro ACTIVE puede
// leerlos (RPC get_permisos_roles). Pre-0052 la RPC no existe → {} y todo
// sigue con los defaults de mezo.
export async function getPermisosRoles() {
  const id = getBusinessId();
  if (!id) return {};
  try {
    const { data, error } = await supabase.rpc('get_permisos_roles', { p_business_id: id });
    if (error) {
      const noExiste = /does not exist|not find|404|PGRST202/i.test(error.message || '');
      if (!noExiste) console.warn('[mezo] get_permisos_roles falló:', error.message);
      return {};
    }
    return (data && typeof data === 'object') ? data : {};
  } catch {
    return {};
  }
}

// Guarda el mapa completo de overrides por rol (RPC set_permisos_roles, solo
// ADMIN; el servidor valida roles/permisos/booleanos y rechaza ADMIN). Si la
// RPC no existe lanzamos Error('sin_migracion_0052') para que la UI lo diga
// claro — "best-effort" no significa fingir que se guardó.
export async function guardarPermisosRoles(permisos) {
  const id = getBusinessId();
  if (!id) throw new Error('Sin negocio activo');
  const { data, error } = await supabase.rpc('set_permisos_roles', {
    p_business_id: id,
    p_permisos:    (permisos && typeof permisos === 'object') ? permisos : {},
  });
  if (error) {
    const msg = error.message || '';
    if (/does not exist|not find|404|PGRST202|schema cache/i.test(msg)) {
      console.warn('[mezo] permisos por rol no guardados (¿falta la migración 0052?):', msg);
      throw new Error('sin_migracion_0052');
    }
    throw new Error(msg || 'No pudimos guardar los permisos por rol');
  }
  return (data && typeof data === 'object') ? data : {};
}

// Rol del usuario en el negocio actual (ADMIN/CASHIER/WAITER/KITCHEN).
export async function getMyRole() {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return null;
  const id = getBusinessId();
  if (!id) return null;
  try {
    const row = one(await restGet(
      `/team_members?user_id=eq.${u.user.id}&business_id=eq.${id}&status=eq.ACTIVE&select=role&limit=1`
    ));
    return row?.role ?? null;
  } catch {
    return null;
  }
}

export async function register(email, password) {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) {
    const code = error.message?.toLowerCase() ?? '';
    // Email enumeration prevention: si el correo ya existe, NO lo revelamos.
    // En vez de tirar error específico, disparamos un OTP de login normal
    // (signInWithOtp) — el usuario real recibe su OTP y puede continuar.
    // Un atacante recibe el MISMO mensaje "Te enviamos un código a tu correo"
    // sin importar si el email existía o no.
    if (code.includes('registered') || code.includes('already')) {
      try {
        await supabase.auth.signInWithOtp({
          email,
          options: { shouldCreateUser: false },
        });
      } catch {
        // Ignoramos cualquier error aquí — el flujo SIEMPRE muestra el mensaje
        // genérico de "te enviamos un código" para no filtrar info.
      }
      return {
        userId: null,
        needsVerification: true,
        accessToken: null,
        refreshToken: null,
        user: null,
        message: 'Te enviamos un código a tu correo',
        _existingUser: true, // hint interno para que Register.jsx sepa el password no servirá
      };
    }
    // Otros errores reales (red, validación de password) sí se muestran.
    toast.error(error.message);
    throw error;
  }
  const needsVerification = !data.session;
  return {
    userId: data.user?.id ?? null,
    needsVerification,
    accessToken:  data.session?.access_token ?? null,
    refreshToken: data.session?.refresh_token ?? null,
    user: data.user ? unwrapUser(data.user) : null,
    message: needsVerification ? 'Te enviamos un código a tu correo' : 'Cuenta creada',
  };
}

export async function verifyOtp(email, code) {
  let { data, error } = await supabase.auth.verifyOtp({
    email, token: String(code).trim(), type: 'signup',
  });
  if (error) {
    const retry = await supabase.auth.verifyOtp({
      email, token: String(code).trim(), type: 'email',
    });
    data  = retry.data;
    error = retry.error;
  }
  if (error) { toast.error(error.message || 'Código incorrecto'); throw error; }

  const session = data.session;
  const user    = data.user;
  if (!session) return data;
  return {
    accessToken:  session.access_token,
    refreshToken: session.refresh_token,
    user: unwrapUser(user),
  };
}

export async function resendOtp(email) {
  const { error } = await supabase.auth.resend({ type: 'signup', email });
  if (error) { toast.error(error.message); throw error; }
  return { message: 'Código reenviado' };
}

export async function login(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    const msg = error.message?.toLowerCase() ?? '';
    if (msg.includes('not confirmed') || msg.includes('not_confirmed')) {
      toast.error('Debes verificar tu correo primero');
    } else {
      toast.error('Correo o contraseña incorrectos');
    }
    throw error;
  }
  await resolveBusiness(data.user.id, data.user.email);
  return {
    accessToken:  data.session.access_token,
    refreshToken: data.session.refresh_token,
    user: unwrapUser(data.user),
  };
}

// ─── Negocio ─────────────────────────────────────────────────────────────────

export async function getNegocio() {
  let id = getBusinessId();
  if (!id) {
    const { data: u } = await supabase.auth.getUser();
    if (!u?.user) return null;
    id = await resolveBusiness(u.user.id, u.user.email);
    if (!id) return null;
  }
  try {
    const negocio = fromNegocio(one(await restGet(`/businesses?id=eq.${id}&select=*&limit=1`)));
    _guardarCache('negocio', negocio);
    return negocio;
  } catch (e) {
    // Sin internet: servir la última config conocida para que el POS arranque.
    if (_esErrorDeRed(e)) {
      const cache = _leerCache('negocio');
      if (cache != null) return cache;
    }
    return null;
  }
}

export async function createNegocio(payload) {
  // Usa la función RPC app_create_business (SECURITY DEFINER): salta RLS
  // por completo. Crea el negocio y agrega al usuario como ADMIN de forma
  // atómica. Resuelve el "new row violates RLS policy" del onboarding.
  const data = await restWrite('POST', '/rpc/app_create_business', {
    p_name:        payload.name,
    p_type:        payload.type ?? 'OTHER',
    p_phone:       payload.phone ?? null,
    p_address:     payload.address ?? null,
    p_city:        payload.city ?? null,
    p_country:     payload.country ?? 'Colombia',
    p_open_at:     payload.openingTime ?? null,
    p_close_at:    payload.closingTime ?? null,
    p_table_count: Number(payload.tableCount) || 0,
  });
  const biz = Array.isArray(data) ? data[0] : data;
  if (!biz?.id) throw new Error('No se pudo crear el negocio');
  setBusinessId(biz.id);

  // Crear las mesas iniciales que el usuario indicó en el onboarding.
  const numMesas = Number(payload.tableCount) || 0;
  if (numMesas > 0) {
    const filas = Array.from({ length: numMesas }, (_, i) => ({
      business_id: biz.id, number: i + 1,
    }));
    await restWrite('POST', '/tables', filas, 'return=minimal').catch(() => {});
  }

  return fromNegocio(biz);
}

export async function updateNegocio(payload) {
  const data = one(await restWrite('PATCH', `/businesses?id=eq.${bid()}`, toNegocioRow(payload)));
  // autopedido_activo / pago_qr_config (migración 0041) en PATCH aparte y
  // best-effort: si las columnas aún no existen, NO rompemos el resto del guardado.
  const extra = {};
  if (payload.autopedidoActivo !== undefined) extra.autopedido_activo = !!payload.autopedidoActivo;
  if (payload.pagoQrConfig     !== undefined) extra.pago_qr_config     = payload.pagoQrConfig ?? {};
  if (Object.keys(extra).length) {
    try {
      await restWrite('PATCH', `/businesses?id=eq.${bid()}`, extra, 'return=minimal');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[mezo] autopedido/pago_qr no guardado (¿falta la migración 0041?):', e?.message);
    }
  }
  return {
    ...fromNegocio(data),
    ...(payload.autopedidoActivo !== undefined ? { autopedidoActivo: !!payload.autopedidoActivo } : {}),
    ...(payload.pagoQrConfig     !== undefined ? { pagoQrConfig: payload.pagoQrConfig ?? {} } : {}),
  };
}

// Sube una foto de evidencia al bucket privado `evidencias-ventas` y
// asocia el URL con la venta correspondiente. Se llama después de
// createVenta. Path: <business_id>/<sale_id>-<timestamp>.jpg
export async function subirEvidenciaVenta(saleId, blob) {
  if (!saleId || !blob) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;

  const businessId = bid();
  const ts = Date.now();
  const path = `${businessId}/${saleId}-${ts}.jpg`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/evidencias-ventas/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'image/jpeg',
        'x-upsert': 'true',
      },
      body: blob,
    },
  );
  if (!res.ok) {
    console.error('subirEvidenciaVenta:', await res.text().catch(() => ''));
    return null;
  }

  // Guardar referencia interna en la venta. Es path relativo — el cliente
  // genera signed URL cuando quiera mostrar la foto (bucket es privado).
  await restWrite('PATCH', `/sales?id=eq.${saleId}&business_id=eq.${businessId}`,
    { evidencia_url: path }, 'return=minimal').catch(() => {});
  return path;
}

// Genera un signed URL temporal para ver una evidencia. Solo se usa
// cuando el admin quiere revisar la foto guardada.
export async function urlEvidenciaVenta(path, segundosValidez = 600) {
  if (!path) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;
  try {
    const res = await fetch(
      `${SUPABASE_URL}/storage/v1/object/sign/evidencias-ventas/${path}?expiresIn=${segundosValidez}`,
      { method: 'POST', headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.signedURL ? `${SUPABASE_URL}/storage/v1${data.signedURL}` : null;
  } catch { return null; }
}

// Sube el logo del negocio a Supabase Storage y actualiza businesses.logo_url.
// El bucket business-logos se crea en la migración 0001 con lectura pública.
export async function uploadLogoNegocio(file) {
  if (!file) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { toast.error('Tu sesión no está activa.'); throw new Error('no_session'); }

  const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
  const businessId = bid();
  const path = `${businessId}/logo.${ext}`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/business-logos/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type || 'image/png',
        'x-upsert': 'true', // sobrescribe si ya existe
      },
      body: file,
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    toast.error('No se pudo subir el logo. Revisa el archivo.');
    throw new Error('upload_failed: ' + txt);
  }

  // Cache busting con timestamp para que el navegador no muestre la anterior
  const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/business-logos/${path}?v=${Date.now()}`;
  await restWrite('PATCH', `/businesses?id=eq.${businessId}`,
    { logo_url: publicUrl }, 'return=minimal');
  return publicUrl;
}

// Sube un PDF del menú al bucket público `menu-pdfs` y guarda la URL en
// businesses.menu_pdf_url. Cuando el dueño activa menu_modo = 'pdf', el menú
// público redirige al PDF en lugar de generar la vista digital.
export async function uploadMenuPdf(file) {
  if (!file) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { toast.error('Tu sesión no está activa.'); throw new Error('no_session'); }

  // Validamos que sea PDF — los menús pueden ser pesados, hasta 10 MB OK.
  if (!/pdf/i.test(file.type) && !file.name?.toLowerCase().endsWith('.pdf')) {
    toast.error('Sube un archivo PDF.');
    throw new Error('not_pdf');
  }

  const businessId = bid();
  const ts = Date.now();
  const path = `${businessId}/menu-${ts}.pdf`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/menu-pdfs/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/pdf',
        'x-upsert': 'true',
      },
      body: file,
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    toast.error('No se pudo subir el menú PDF.');
    throw new Error('upload_failed: ' + txt);
  }

  const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/menu-pdfs/${path}`;
  await restWrite('PATCH', `/businesses?id=eq.${businessId}`,
    { menu_pdf_url: publicUrl, menu_modo: 'pdf' }, 'return=minimal');
  return publicUrl;
}

// Sube una imagen de fondo opcional para mostrar arriba del menú público.
// Va al bucket `business-logos` (público, reusamos el de logos para no crear
// otro bucket aparte). Guarda la URL en businesses.menu_imagen_header.
export async function uploadMenuHeader(file) {
  if (!file) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { toast.error('Tu sesión no está activa.'); throw new Error('no_session'); }

  if (!/image/.test(file.type)) {
    toast.error('Sube una imagen (JPG o PNG).');
    throw new Error('not_image');
  }

  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
  const businessId = bid();
  const ts = Date.now();
  const path = `${businessId}/menu-header-${ts}.${ext}`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/business-logos/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type,
        'x-upsert': 'true',
      },
      body: file,
    },
  );
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    toast.error('No pudimos subir la imagen.');
    throw new Error('upload_failed: ' + txt);
  }

  const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/business-logos/${path}`;
  await restWrite('PATCH', `/businesses?id=eq.${businessId}`,
    { menu_imagen_header: publicUrl }, 'return=minimal');
  return publicUrl;
}

// Sube la foto de UN producto al bucket público business-logos (carpeta
// productos/) y devuelve la URL pública. El caller la guarda con
// create/updateProducto({ imagenUrl }). Antes el form de productos tenía
// imagen: null hardcodeado — las fotos subidas se perdían silenciosamente.
export async function uploadImagenProducto(file) {
  if (!file) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) { toast.error('Tu sesión no está activa.'); throw new Error('no_session'); }
  if (!/image/.test(file.type)) {
    toast.error('Sube una imagen (JPG o PNG).');
    throw new Error('not_image');
  }

  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
  const businessId = bid();
  const rnd = Math.random().toString(36).slice(2, 8);
  const path = `${businessId}/productos/${Date.now()}-${rnd}.${ext}`;

  const res = await fetch(
    `${SUPABASE_URL}/storage/v1/object/business-logos/${path}`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type,
        'x-upsert': 'true',
      },
      body: file,
    },
  );
  if (!res.ok) {
    toast.error('No pudimos subir la imagen.');
    throw new Error('upload_failed');
  }
  return `${SUPABASE_URL}/storage/v1/object/public/business-logos/${path}`;
}

// Pide a la Edge Function sugerir-imagen hasta 6 fotos de Pexels para el
// nombre de un producto. Devuelve [{id, thumb, url, alt, fotografo}].
// La API key de Pexels vive en el server — aquí solo viaja la query.
export async function sugerirImagenes(query) {
  if (!query?.trim()) return [];
  const { data, error } = await supabase.functions.invoke('sugerir-imagen', {
    body: { query: query.trim() },
  });
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error ?? 'sugerir_imagen_failed');
  return data.fotos ?? [];
}

// Registra una visita al menú QR. Llamada desde MenuPublico.jsx cuando
// alguien escanea el QR y ve el menú. No requiere auth (anon-accessible).
export async function trackMenuView(businessId) {
  if (!businessId) return;
  try {
    await supabase.rpc('track_menu_view', { p_business_id: businessId });
  } catch {
    // silencioso — si falla no rompe la visualización del menú
  }
}

// ─── Productos ───────────────────────────────────────────────────────────────

export async function getProductos() {
  return _conCache('productos', async () => {
    const rows = await restGet(`/products?business_id=eq.${bid()}&select=*&order=created_at.asc`);
    return (rows ?? []).map(fromProduct);
  });
}

export async function createProducto(payload) {
  await _verificarLimite('productos', await _contar('products'));
  const row = { ...toProductRow(payload), business_id: bid() };
  const creado = fromProduct(one(await restWrite('POST', '/products', row)));
  await _guardarRecetaProducto(creado.id, payload);   // receta en PATCH aparte
  return {
    ...creado,
    ...(payload.inventarioAsociado !== undefined ? { inventarioAsociado: payload.inventarioAsociado } : {}),
    ...(payload.recetaPasos !== undefined ? { recetaPasos: payload.recetaPasos } : {}),
    ...(payload.pasaPorCocina !== undefined ? { pasaPorCocina: payload.pasaPorCocina !== false } : {}),
    ...(payload.modificadores !== undefined ? { modificadores: payload.modificadores ?? [] } : {}),
  };
}

export async function updateProducto(id, payload) {
  const data = one(await restWrite('PATCH',
    `/products?id=eq.${id}&business_id=eq.${bid()}`, toProductRow(payload)));
  await _guardarRecetaProducto(id, payload);          // receta en PATCH aparte
  return {
    ...fromProduct(data),
    ...(payload.inventarioAsociado !== undefined ? { inventarioAsociado: payload.inventarioAsociado } : {}),
    ...(payload.recetaPasos !== undefined ? { recetaPasos: payload.recetaPasos } : {}),
    ...(payload.pasaPorCocina !== undefined ? { pasaPorCocina: payload.pasaPorCocina !== false } : {}),
    ...(payload.modificadores !== undefined ? { modificadores: payload.modificadores ?? [] } : {}),
  };
}

export async function deleteProducto(id) {
  await restWrite('DELETE', `/products?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

// ─── Categorías ──────────────────────────────────────────────────────────────

export async function getCategorias() {
  return _conCache('categorias', async () => {
    const rows = await restGet(`/categories?business_id=eq.${bid()}&select=*&order=orden.asc`);
    return (rows ?? []).map(fromCategoria);
  });
}

export async function createCategoria(payload) {
  await _verificarLimite('categorias', await _contar('categories'));
  const row = { ...toCategoriaRow(payload), business_id: bid() };
  return fromCategoria(one(await restWrite('POST', '/categories', row)));
}

export async function updateCategoria(id, payload) {
  const data = one(await restWrite('PATCH',
    `/categories?id=eq.${id}&business_id=eq.${bid()}`, toCategoriaRow(payload)));
  return fromCategoria(data);
}

export async function deleteCategoria(id) {
  await restWrite('DELETE', `/categories?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

// ─── Mesas ───────────────────────────────────────────────────────────────────

async function fetchActiveOrdersRaw() {
  try {
    const rows = await restGet(
      `/orders?business_id=eq.${bid()}&status=in.(OPEN,PREPARING,READY,DELIVERED)&select=*,order_lines(*)`
    );
    return rows ?? [];
  } catch {
    return [];
  }
}

export async function getMesas() {
  // No usamos .catch en tables: si la red falla queremos que _conCache lo
  // detecte y sirva la última copia (no cachear [] que borraría el mapa).
  // El estado de órdenes activas sí puede degradar a [] (es estado vivo).
  return _conCache('mesas', async () => {
    const [tables, active] = await Promise.all([
      restGet(`/tables?business_id=eq.${bid()}&select=*&order=number.asc`),
      fetchActiveOrdersRaw(),
    ]);
    return (tables ?? []).map((t) => fromTable(t, active));
  });
}

export async function createMesa(data = {}) {
  await _verificarLimite('mesas', await _contar('tables'));
  const existing = await restGet(
    `/tables?business_id=eq.${bid()}&select=number&order=number.desc&limit=1`
  ).catch(() => []);
  const next = (one(existing)?.number ?? 0) + 1;
  const row = { business_id: bid(), number: next };
  if (data.zonaId) row.zone_id = data.zonaId;   // zonas reales (0044)
  try {
    return fromTable(one(await restWrite('POST', '/tables', row)));
  } catch (e) {
    // Si zone_id aún no existe (0044 sin correr), reintenta sin zona.
    if (!row.zone_id) throw e;
    delete row.zone_id;
    return fromTable(one(await restWrite('POST', '/tables', row)));
  }
}

export async function deleteMesa(id) {
  await restWrite('DELETE', `/tables?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

export async function updateMesa(id, data) {
  const update = {};
  if (data.estado !== undefined)    update.estado     = data.estado;
  if (data.ocupadaEn !== undefined) update.ocupada_en = data.ocupadaEn;
  if (data.personas !== undefined)  update.personas   = data.personas;
  if (data.posX !== undefined)      update.pos_x      = data.posX;   // plano (0043)
  if (data.posY !== undefined)      update.pos_y      = data.posY;
  if (data.zonaId !== undefined)    update.zone_id    = data.zonaId; // zonas (0044)
  if (Object.keys(update).length === 0) return { id, ...data };
  await restWrite('PATCH', `/tables?id=eq.${id}&business_id=eq.${bid()}`,
    update, 'return=minimal');
  return { id, ...data };
}

export async function cambiarMesa(origenId, destinoId) {
  const ord = one(await restGet(
    `/orders?business_id=eq.${bid()}&table_id=eq.${origenId}` +
    `&status=in.(OPEN,PREPARING,READY,DELIVERED)&select=id&order=created_at.desc&limit=1`
  ));
  if (!ord) { toast.error('No hay orden activa en esta mesa.'); throw new Error('no_order'); }
  // Filtro defensivo business_id — defensa en profundidad además de RLS.
  await restWrite('PATCH',
    `/orders?id=eq.${ord.id}&business_id=eq.${bid()}`,
    { table_id: destinoId }, 'return=minimal');
  return { ok: true };
}

// ─── Zonas (tabla real desde 0044 — antes eran mock y la UI fingía) ──────────

function fromZona(z) {
  return { id: z.id, nombre: z.nombre, color: z.color ?? '#C8903F', orden: z.orden ?? 0 };
}

export async function getZonas() {
  try {
    const rows = await restGet(`/zones?business_id=eq.${bid()}&select=*&order=orden.asc,created_at.asc`);
    return (rows ?? []).map(fromZona);
  } catch {
    // Migración 0044 sin correr → sin zonas (la UI muestra solo "Todas").
    return [];
  }
}

export async function createZona(d) {
  const row = { business_id: bid(), nombre: d.nombre, color: d.color ?? '#C8903F', orden: d.orden ?? 0 };
  return fromZona(one(await restWrite('POST', '/zones', row)));
}

export async function updateZona(id, d) {
  const update = {};
  if (d.nombre !== undefined) update.nombre = d.nombre;
  if (d.color  !== undefined) update.color  = d.color;
  if (d.orden  !== undefined) update.orden  = d.orden;
  return fromZona(one(await restWrite('PATCH', `/zones?id=eq.${id}&business_id=eq.${bid()}`, update)));
}

export async function deleteZona(id) {
  await restWrite('DELETE', `/zones?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

export async function getCuentas()        { return []; }
export async function createCuenta(d)     { return { id: `tmp-${Date.now()}`, ...d, creadoEn: new Date() }; }
export async function updateCuenta(id, d) { return { id, ...d }; }
export async function getCuenta()         { return null; }

// ─── Órdenes ─────────────────────────────────────────────────────────────────

export async function getOrdenes(params = {}) {
  // team_members(name): quién tomó la orden (para reportes por empleado).
  // DEFENSIVO: si el embed falla (caché de esquema de PostgREST, relación no
  // resuelta), reintentamos SIN el embed — cargar las órdenes es camino
  // crítico del POS y jamás puede caerse por un dato "nice to have".
  let filtros = '';
  if (params.desde) filtros += `&created_at=gte.${new Date(params.desde).toISOString()}`;
  if (params.hasta) {
    const h = new Date(params.hasta);
    h.setHours(23, 59, 59, 999);
    filtros += `&created_at=lte.${h.toISOString()}`;
  }
  const base = `/orders?business_id=eq.${bid()}&order=created_at.desc${filtros}`;
  try {
    const rows = await restGet(`${base}&select=*,order_lines(*),team_members(name)`);
    return (rows ?? []).map(fromOrder);
  } catch (e) {
    console.warn('[mezo] embed team_members falló, reintentando sin él:', e?.message);
    const rows = await restGet(`${base}&select=*,order_lines(*)`);
    return (rows ?? []).map(fromOrder);
  }
}

// Paso 1: crear orden → OPEN
export async function createOrden(payload) {
  const lines = (payload.items ?? payload.lines ?? []).map((i) => ({
    product_id:   i.productId ?? i.id ?? null,
    product_name: i.nombre ?? i.name ?? 'Producto',
    unit_price:   Number(i.precio ?? i.unitPrice ?? 0),
    quantity:     Number(i.cantidad ?? i.quantity ?? 1),
    subtotal:     Number((i.precio ?? i.unitPrice ?? 0) * (i.cantidad ?? i.quantity ?? 1)) - Math.max(0, Math.round(Number(i.descuentoMonto ?? i.descuento_monto ?? 0))),
    notas:        i.nota ?? i.notas ?? null,
    // Descuento con motivo (0062). El servidor vuelve a fijar subtotal = bruto − descuento.
    descuento_monto:  Math.max(0, Math.round(Number(i.descuentoMonto ?? i.descuento_monto ?? 0))),
    descuento_motivo: i.descuentoMotivo ?? i.descuento_motivo ?? null,
    // Quién autorizó el descuento con su PIN (0067); el trigger lo valida.
    descuento_autorizado_id:  i.descuentoAutorizadoId ?? i.descuento_autorizado_id ?? null,
    descuento_autorizado_por: i.descuentoAutorizadoPor ?? i.descuento_autorizado_por ?? null,
  }));

  const ord = one(await restWrite('POST', '/orders', {
    business_id: bid(),
    table_id:    payload.tableId ?? null,
    status:      'OPEN',
    total:       lines.reduce((s, l) => s + l.subtotal, 0),
    tip:         0,
    // Quién tomó la orden (team_members.id) — para reportes por empleado.
    employee_id: payload.employeeId ?? null,
  }));

  if (lines.length) {
    try {
      await _insertarLineas(lines.map((l) => ({ ...l, order_id: ord.id })));
    } catch (e) {
      // Si el servidor rechaza una línea (p. ej. descuento sin autorización,
      // 0067), no dejamos una orden OPEN vacía huérfana en la mesa/caja.
      // PATCH y no DELETE: orders_delete exige ventas.anular (el cajero no lo
      // tiene) y PostgREST respondería 204 con 0 filas; cancelar sí lo permite
      // orders_update para cualquier miembro, y CANCELLED no cuenta como activa.
      try { await restWrite('PATCH', `/orders?id=eq.${ord.id}&business_id=eq.${bid()}`, { status: 'CANCELLED' }, 'return=minimal'); } catch { /* best-effort */ }
      throw e;
    }
  }

  const full = one(await restGet(`/orders?id=eq.${ord.id}&select=*,order_lines(*)&limit=1`));
  return fromOrder(full);
}

// Devuelve la orden activa (OPEN/PREPARING/READY/DELIVERED) de una mesa o null.
// La usamos para anexar líneas en lugar de crear órdenes duplicadas cuando
// el mesero vuelve a "Agregar productos" en una mesa ya ocupada.
export async function getOrdenAbiertaPorMesa(tableId) {
  if (!tableId) return null;
  try {
    const row = one(await restGet(
      `/orders?business_id=eq.${bid()}&table_id=eq.${tableId}` +
      `&status=in.(OPEN,PREPARING,READY,DELIVERED)` +
      `&select=*,order_lines(*)&order=created_at.desc&limit=1`
    ));
    return row ? fromOrder(row) : null;
  } catch {
    return null;
  }
}

// Envía un pedido de mesa a cocina: crea una nueva orden OPEN si la mesa
// no tiene una activa, o anexa las líneas a la existente. Devuelve el id
// de la orden resultante. NO cierra la orden — eso lo hace `createVenta`
// cuando el cajero cobra la mesa.
export async function enviarPedidoMesa(tableId, items, opts = {}) {
  if (!tableId)         throw new Error('table_required');
  if (!items?.length)   throw new Error('items_required');

  const lines = items.map((i) => ({
    product_id:   i.productId ?? i.id ?? null,
    product_name: i.nombre ?? i.name ?? 'Producto',
    unit_price:   Number(i.precio ?? i.unitPrice ?? 0),
    quantity:     Number(i.cantidad ?? i.quantity ?? 1),
    subtotal:     Number((i.precio ?? i.unitPrice ?? 0) * (i.cantidad ?? i.quantity ?? 1)) - Math.max(0, Math.round(Number(i.descuentoMonto ?? i.descuento_monto ?? 0))),
    notas:        i.nota ?? i.notas ?? null,
    // Descuento con motivo (0062). El servidor vuelve a fijar subtotal = bruto − descuento.
    descuento_monto:  Math.max(0, Math.round(Number(i.descuentoMonto ?? i.descuento_monto ?? 0))),
    descuento_motivo: i.descuentoMotivo ?? i.descuento_motivo ?? null,
    // Quién autorizó el descuento con su PIN (0067); el trigger lo valida.
    descuento_autorizado_id:  i.descuentoAutorizadoId ?? i.descuento_autorizado_id ?? null,
    descuento_autorizado_por: i.descuentoAutorizadoPor ?? i.descuento_autorizado_por ?? null,
  }));
  const sumaNuevas = lines.reduce((s, l) => s + l.subtotal, 0);

  // Buscar orden activa. Incluye DELIVERED: si la mesa ya pidió la cuenta y
  // el cliente ordena algo más, se anexa a la MISMA orden (antes se creaba una
  // segunda orden en la mesa → una quedaba sin cobrar y la mesa no se liberaba).
  const existente = one(await restGet(
    `/orders?business_id=eq.${bid()}&table_id=eq.${tableId}` +
    `&status=in.(OPEN,PREPARING,READY,DELIVERED)` +
    `&select=id,total,status&order=created_at.desc&limit=1`
  ).catch(() => []));

  let ordenId;
  let totalFinal;

  if (existente?.id) {
    // Anexar líneas a la orden existente
    ordenId    = existente.id;
    totalFinal = (existente.total ?? 0) + sumaNuevas;
    await _insertarLineas(lines.map((l) => ({ ...l, order_id: ordenId })));
    // Si la cocina ya la había marcado lista (READY) o estaba servida
    // (DELIVERED), la REABRIMOS: sin esto la nueva ronda jamás aparecía en
    // la Pantalla de Cocina (filtra estadoCocina 'listo').
    if (existente.status === 'READY' || existente.status === 'DELIVERED') {
      await restWrite('PATCH', `/orders?id=eq.${ordenId}&business_id=eq.${bid()}`,
        { status: 'OPEN' }, 'return=minimal').catch(() => {});
    }
    await restWrite('PATCH', `/orders?id=eq.${ordenId}&business_id=eq.${bid()}`,
      { total: totalFinal }, 'return=minimal');
  } else {
    // Crear orden nueva OPEN sobre la mesa
    const ord = one(await restWrite('POST', '/orders', {
      business_id: bid(),
      table_id:    tableId,
      status:      'OPEN',
      total:       sumaNuevas,
      tip:         0,
      employee_id: opts.employeeId ?? null,   // quién tomó el pedido
    }));
    ordenId    = ord.id;
    totalFinal = sumaNuevas;
    await _insertarLineas(lines.map((l) => ({ ...l, order_id: ordenId })));
  }

  // Asegurar que la mesa quede marcada como ocupada (defensa: si el mesero
  // saltó "Ocupar" y fue directo a tomar pedido).
  await restWrite('PATCH', `/tables?id=eq.${tableId}&business_id=eq.${bid()}`,
    { estado: 'ocupada', ocupada_en: new Date().toISOString() },
    'return=minimal').catch(() => {});

  return { ordenId, total: totalFinal };
}

// Paso 2: marcar como entregada → DELIVERED
export async function deliverOrden(id) {
  const data = one(await restWrite('PATCH',
    `/orders?id=eq.${id}&business_id=eq.${bid()}&select=*,order_lines(*)`,
    { status: 'DELIVERED' }));
  return fromOrder(data);
}

// Cancelar una orden (status='CANCELLED'). Se usa cuando el cliente decide
// no consumir lo que pidió y vamos a liberar la mesa sin cobrar. NO genera
// venta. La orden queda en historial con su status='CANCELLED'.
export async function cancelarOrden(orderId) {
  if (!orderId) return null;
  try {
    await restWrite('PATCH', `/orders?id=eq.${orderId}&business_id=eq.${bid()}`,
      { status: 'CANCELLED' }, 'return=minimal');
    return { ok: true };
  } catch (e) {
    console.error('[cancelarOrden]', e);
    throw e;
  }
}

// Quita UNA línea de una orden activa (por ej. el cliente se arrepintió de
// un producto). Recalcula el total de la orden sumando las líneas restantes.
// El frontend muestra el botón de quitar solo en mesas ocupadas con orden
// activa (no en mesas pagando/CLOSED).
export async function quitarLineaOrden(orderId, lineId) {
  if (!orderId || !lineId) throw new Error('order_id_y_line_id_requeridos');
  const businessId = bid();
  // 1) Borrar la línea (RLS valida que la orden sea de este business)
  await restWrite('DELETE',
    `/order_lines?id=eq.${lineId}&order_id=eq.${orderId}`,
    null, 'return=minimal');
  // 2) Recalcular total sumando las líneas restantes
  const restantes = await restGet(
    `/order_lines?order_id=eq.${orderId}&select=subtotal`
  ).catch(() => []);
  const nuevoTotal = (restantes ?? []).reduce((s, l) => s + (l.subtotal ?? 0), 0);
  await restWrite('PATCH',
    `/orders?id=eq.${orderId}&business_id=eq.${businessId}`,
    { total: nuevoTotal }, 'return=minimal');
  return { ok: true, total: nuevoTotal };
}

// Paso 3: cerrar venta con método de pago.
//
// Usa la RPC SECURITY DEFINER `mezo_cerrar_venta` (migración 0028): recalcula
// el subtotal DESDE order_lines en el servidor — el cliente ya no puede
// manipular el total — y cierra orden + crea venta atómicamente.
//
// `descuento` es el canje de fidelidad (lo valida el servidor: nunca > subtotal).
export async function createVenta(payload) {
  const tip       = Math.round(Number(payload.tip?.amount ?? payload.tip ?? 0));
  const descuento = Math.round(Number(payload.descuento ?? 0));
  const pm        = normalizePM(payload.paymentMethod);

  const { data, error } = await supabase.rpc('mezo_cerrar_venta', {
    p_order_id:       payload.orderId,
    p_payment_method: pm,
    p_tip:            tip,
    p_descuento:      descuento,
    p_comentario:     payload.comentario ?? null,
    p_metodo_nombre:  payload.metodoNombre ?? null,
    p_pagos:          payload.pagos ?? null,
  });

  if (error) {
    // Fallback al flujo viejo SOLO si la RPC aún no está desplegada (404 /
    // function does not exist). Así un deploy a medias no bloquea el cobro.
    const noExiste = /does not exist|not find|404|PGRST202/i.test(error.message || '');
    if (!noExiste) {
      console.error('[createVenta] mezo_cerrar_venta:', error);
      toast.error('No pudimos cerrar la venta. Intenta de nuevo.');
      throw error;
    }
    return _createVentaLegacy(payload, tip, pm);
  }

  // Inventario maestro (0051): sonda al servidor. Si la RPC existe, el trigger
  // sobre sales YA descontó la receta de cada línea y el cliente no vuelve a
  // descontar (CarritoPOS llama descontarPorVenta, que consulta este registro).
  // Si no existe (404), sigue el camino de hoy: descuento en el cliente.
  // Nunca lanza ni bloquea el cobro.
  await inventarioService.descontarEnServidor(data?.order_id ?? payload.orderId);

  return {
    id:            data.id,
    saleNumber:    null,
    orderId:       data.order_id,
    paymentMethod: data.payment_method,
    subtotal:      data.subtotal ?? 0,
    tip:           data.tip ?? null,
    total:         data.total,
    amountPaid:    payload.amountPaid ?? null,
    change:        payload.change ?? null,
    createdAt:     data.created_at ? new Date(data.created_at) : new Date(),
  };
}

// Flujo anterior (cliente decide el total). Solo se usa como fallback si la
// RPC mezo_cerrar_venta no está desplegada todavía. Se elimina cuando 0028
// esté confirmada en producción.
async function _createVentaLegacy(payload, tip, pm) {
  const ord = one(await restGet(
    `/orders?id=eq.${payload.orderId}&business_id=eq.${bid()}&select=*,order_lines(*)&limit=1`
  ));
  if (!ord) { toast.error('No se encontró la orden'); throw new Error('no_order'); }
  const total = (ord.total ?? 0) + tip - Math.round(Number(payload.descuento ?? 0));
  await restWrite('PATCH', `/orders?id=eq.${payload.orderId}&business_id=eq.${bid()}`,
    { status: 'CLOSED', payment_method: pm, tip }, 'return=minimal');
  // SOLO columnas del esquema base: este camino corre justo cuando las
  // migraciones nuevas NO están (la RPC dio 404) — mandar comentario o
  // metodo_nombre aquí revienta el POST con 400 y bloquea el cobro entero.
  const sale = one(await restWrite('POST', '/sales', {
    business_id: bid(), order_id: payload.orderId, payment_method: pm,
    total: Math.max(0, total), tip,
  }));
  // Misma sonda de inventario (0051) que el camino principal.
  await inventarioService.descontarEnServidor(payload.orderId);
  return {
    id: sale.id, saleNumber: null, orderId: sale.order_id,
    paymentMethod: sale.payment_method, subtotal: ord.total ?? 0,
    tip: sale.tip ?? null, total: sale.total,
    amountPaid: payload.amountPaid ?? null, change: payload.change ?? null,
    createdAt: sale.created_at ? new Date(sale.created_at) : new Date(),
  };
}

// ─── Modo offline: venta directa atómica + cola (Fase 2) ─────────────────────
// crearVentaDirecta sube en UNA llamada (RPC mezo_venta_directa, migración 0032)
// orden + líneas + venta. Es idempotente por client_local_id, así que es seguro
// reintentarla. Se usa SOLO para reenviar la cola offline — el flujo online
// normal del POS sigue usando createOrden + createVenta sin cambios.

function _localId() {
  // Id local único por dispositivo para idempotencia del reenvío.
  return `loc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export async function crearVentaDirecta(intent) {
  const { data, error } = await supabase.rpc('mezo_venta_directa', {
    p_business_id:     intent.businessId ?? bid(),
    p_items:           intent.items,
    p_payment_method:  intent.paymentMethod,
    p_tip:             Math.round(Number(intent.tip ?? 0)),
    p_descuento:       Math.round(Number(intent.descuento ?? 0)),
    p_table_id:        intent.tableId ?? null,
    p_client_local_id: intent.localId,
    p_comentario:      intent.comentario ?? null,
    p_metodo_nombre:   intent.metodoNombre ?? null,
    p_pagos:           intent.pagos ?? null,
  });
  if (error) throw error;

  // Descontar inventario asociado a la venta — solo si se creó AHORA (no en un
  // reintento idempotente, para no descontar dos veces). Replica lo que hace el
  // POS online tras cobrar. Fire-and-forget por ítem.
  if (data && !data.idempotente && Array.isArray(intent.deducciones)) {
    // Inventario maestro (0051): si el servidor ya descontó (trigger sobre
    // sales, también dispara con mezo_venta_directa), el cliente no repite;
    // si la RPC no existe todavía, descuenta como antes.
    const srv = await inventarioService.descontarEnServidor(data.order_id);
    if (srv.modo !== 'servidor') {
      for (const d of intent.deducciones) {
        if (d?.itemId && d.cantidad > 0) {
          try { await inventarioService.descontarPorVenta(d.itemId, d.cantidad, data.order_id); }
          catch { /* no romper el sync por un descuento de inventario */ }
        }
      }
    }
    // Emitir factura electrónica DIAN de la venta recién sincronizada.
    if (data.id) emitirFacturaDian(data.id);
  }
  return data;
}

// Dispara la emisión de factura electrónica DIAN para una venta ya cerrada.
// Fire-and-forget: NUNCA bloquea ni rompe el cobro. La edge function valida si
// el negocio tiene la facturación activa y, si no, no hace nada.
export function emitirFacturaDian(saleId) {
  if (!saleId) return;
  try {
    supabase.functions.invoke('facturar-dian', { body: { saleId } }).catch(() => {});
  } catch { /* sin red / sin función: la venta ya quedó registrada */ }
}

// Guarda una venta hecha sin internet. Devuelve el intent con su localId.
export function encolarVentaOffline(intent) {
  const venta = {
    ...intent,
    localId:    intent.localId ?? _localId(),
    businessId: intent.businessId ?? getBusinessId(),
    createdAt:  new Date().toISOString(),
  };
  encolarVenta(venta);
  return venta;
}

export function ventasOfflinePendientes() { return ventasPendientes(); }
export function contarVentasOffline()     { return contarPendientes(); }

// Reenvía la cola de ventas offline. La llama el indicador de conexión cuando
// vuelve el internet. Devuelve { ok, fail }.
export async function sincronizarVentasOffline() {
  return _sincronizarCola(crearVentaDirecta);
}

// ─── Inventario maestro (0051) ───────────────────────────────────────────────
// Carga masiva por nombre normalizado, kardex de todo el negocio y cobertura
// de recetas. La lógica vive en inventarioService (módulo del inventario);
// aquí solo se expone con paridad mockApi ↔ barrel.
export const cargarInventarioMaestro = (filas, opts) => inventarioService.cargarMaestro(filas, opts);
export const getKardexInventario     = (filtros)     => inventarioService.kardex(filtros);
export const getCoberturaRecetas     = ()            => inventarioService.coberturaRecetas();

export async function updateOrden(id, data) {
  if (data.estadoCocina === 'listo') {
    const r = one(await restWrite('PATCH',
      `/orders?id=eq.${id}&business_id=eq.${bid()}&select=*,order_lines(*)`,
      { status: 'READY' }));
    return fromOrder(r);
  }
  const update = {};
  if (data.tableId !== undefined) update.table_id = data.tableId;
  if (Object.keys(update).length === 0) return null;

  const r = one(await restWrite('PATCH',
    `/orders?id=eq.${id}&business_id=eq.${bid()}&select=*,order_lines(*)`, update));
  return fromOrder(r);
}

// ─── Empleados ───────────────────────────────────────────────────────────────

// 'cajera' es el id histórico que devuelve fromEmpleado; 'cajero' el que usa
// el formulario de empleados. Ambos → CASHIER.
const ROLE_FRONT_TO_DB = { admin: 'ADMIN', cajera: 'CASHIER', cajero: 'CASHIER', mesero: 'WAITER', cocina: 'KITCHEN' };

export async function getEmpleados() {
  const rows = await restGet(`/team_members?business_id=eq.${bid()}&select=*&order=invited_at.asc`);
  return (rows ?? []).map(fromEmpleado);
}

export async function createEmpleado(payload) {
  await _verificarLimite('empleados', await _contar('team_members'));
  const rol = (payload.roles?.[0] ?? 'cajera').toLowerCase();
  const data = one(await restWrite('POST', '/team_members', {
    business_id: bid(),
    email:       payload.correo,
    name:        payload.nombre ?? payload.correo?.split('@')[0] ?? null,
    role:        ROLE_FRONT_TO_DB[rol] ?? 'CASHIER',
    status:      'INVITED',
  }));
  // El PIN NO va en el INSERT: desde 0053 la columna en claro tiene un CHECK
  // que exige null y el insert entero fallaría. Se fija aparte y best-effort —
  // que el empleado quede creado importa más que el PIN, que se puede volver
  // a poner desde la ficha.
  if (payload.pin && data?.id) {
    try { await setPinEmpleado(data.id, payload.pin); }
    catch (e) { console.warn('[mezo] empleado creado pero el PIN no se guardó:', e?.message); }
  }
  // Overrides de permisos (0049): PATCH aparte y best-effort para que crear
  // el empleado nunca falle por una columna que aún no existe.
  const permisos = payload.permisos;
  if (permisos && typeof permisos === 'object' && Object.keys(permisos).length && data?.id) {
    const conPermisos = await _patchPermisosEmpleado(data.id, permisos);
    return fromEmpleado(conPermisos ?? { ...data, permisos });
  }
  return fromEmpleado(data);
}

export async function updateEmpleado(id, payload) {
  const update = {};
  if (payload.roles?.length) {
    const rol = payload.roles[0].toLowerCase();
    update.role = ROLE_FRONT_TO_DB[rol] ?? 'CASHIER';
  }
  if (payload.activo !== undefined) update.status = payload.activo ? 'ACTIVE' : 'INACTIVE';
  if (payload.nombre) update.name = payload.nombre;
  // El PIN sale del PATCH y se fija por RPC (0053). `undefined` = no se toca;
  // '' o null = se borra, que es el "desbloquear" del admin.
  const pinNuevo = payload.pin;

  // Overrides de permisos (0049) van en un PATCH APARTE y best-effort: si la
  // migración no ha corrido, la columna no existe y el PATCH falla con 400 —
  // lo tragamos para que nombre/rol/PIN se guarden igual (misma idea que
  // _guardarRecetaProducto). Sin toast: el error de esquema no le sirve al
  // dueño; queda en consola para el desarrollador.
  const tienePermisos = payload.permisos !== undefined;
  const tocaPin       = pinNuevo !== undefined;
  if (Object.keys(update).length === 0 && !tienePermisos && !tocaPin) return null;

  let data = null;
  if (Object.keys(update).length > 0) {
    data = one(await restWrite('PATCH',
      `/team_members?id=eq.${id}&business_id=eq.${bid()}`, update));
  }
  if (tienePermisos) {
    const permisos = (payload.permisos && typeof payload.permisos === 'object') ? payload.permisos : {};
    const guardado = await _patchPermisosEmpleado(id, permisos);
    if (guardado) data = guardado;
    else if (data) data = { ...data, permisos };   // optimista: la UI ya los muestra
  }
  // El PIN va por su propia vía (RPC hasheada) y al final: si falla, el resto
  // del guardado ya quedó hecho y el dueño puede reintentar solo el PIN.
  let pinPuesto = null;
  if (tocaPin) {
    try { pinPuesto = (await setPinEmpleado(id, pinNuevo)).tienePin; }
    catch (e) { console.warn('[mezo] el PIN no se pudo guardar:', e?.message); }
  }
  if (!data) return null;
  const salida = fromEmpleado(data);
  return pinPuesto === null ? salida : { ...salida, tienePin: pinPuesto };
}

// PATCH silencioso de team_members.permisos (sin el toast de restWrite).
// Devuelve la fila actualizada o null si la columna aún no existe (pre-0049).
async function _patchPermisosEmpleado(id, permisos) {
  try {
    const headers = { ...(await authHeaders()), Prefer: 'return=representation' };
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/team_members?id=eq.${id}&business_id=eq.${bid()}`,
      { method: 'PATCH', headers, body: JSON.stringify({ permisos }) },
    );
    const text = await res.text();
    let json; try { json = text ? JSON.parse(text) : null; } catch { json = null; }
    if (!res.ok) {
      console.warn('[mezo] permisos del empleado no guardados (¿falta la migración 0049?):', json?.message || res.status);
      return null;
    }
    return one(json);
  } catch (e) {
    console.warn('[mezo] permisos del empleado no guardados:', e?.message);
    return null;
  }
}

export async function deleteEmpleado(id) {
  await restWrite('DELETE', `/team_members?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

// ─── Movimientos ─────────────────────────────────────────────────────────────

export async function getMovimientos(fecha) {
  let path = `/movements?business_id=eq.${bid()}&select=*&order=creado_en.desc`;
  if (fecha) {
    const d = new Date(fecha);
    const start = new Date(d); start.setHours(0, 0, 0, 0);
    const end   = new Date(d); end.setHours(23, 59, 59, 999);
    path += `&creado_en=gte.${start.toISOString()}&creado_en=lte.${end.toISOString()}`;
  }
  const rows = await restGet(path);
  return (rows ?? []).map(fromMovimiento);
}

export async function createMovimiento(payload) {
  const data = one(await restWrite('POST', '/movements', {
    business_id: bid(),
    tipo:        payload.tipo,
    descripcion: payload.descripcion,
    monto:       Number(payload.monto) || 0,
    metodo:      payload.metodo ?? 'CASH',
  }));
  return fromMovimiento(data);
}

// ─── Turnos ──────────────────────────────────────────────────────────────────

export async function getTurnoActivo() {
  try {
    const row = one(await restGet(
      `/shifts?business_id=eq.${bid()}&fin=is.null&select=*&order=inicio.desc&limit=1`
    ));
    return fromShift(row);
  } catch {
    return null;
  }
}

export async function createTurno(payload) {
  const data = one(await restWrite('POST', '/shifts', {
    business_id:     bid(),
    empleado_id:     payload.empleadoId ?? null,
    empleado_nombre: payload.empleadoNombre ?? null,
    roles:           payload.roles ?? ['cajera'],
    base_turno:      Number(payload.baseTurno) || 0,
  }));
  return fromShift(data);
}

export async function updateTurno(shiftId, payload) {
  const update = {};
  if (payload.fin)                 update.fin = new Date(payload.fin).toISOString();
  if (payload.duracionMin != null) update.duracion_min = payload.duracionMin;
  if (payload.numOrdenes != null)  update.num_ordenes  = payload.numOrdenes;
  if (payload.nota !== undefined)  update.nota         = payload.nota;
  if (Object.keys(update).length === 0) return null;

  const data = one(await restWrite('PATCH',
    `/shifts?id=eq.${shiftId}&business_id=eq.${bid()}`, update));
  return fromShift(data);
}

// ─── PIN de empleados (migración 0053) ──────────────────────────────────────
// El PIN dejó de guardarse en claro: la base lo hashea (bcrypt vía pgcrypto) y
// solo expone si existe y si está bloqueado por intentos fallidos. Un PIN de 4
// dígitos son 10.000 combinaciones, así que el hash sin límite de intentos
// sería teatro — el bloqueo temporal lo pone la propia migración.
//
// Las dos funciones aquí abajo funcionan ANTES y DESPUÉS de correr 0053: si la
// RPC no existe todavía (404 / 42883), caen al camino viejo de escribir la
// columna en claro. Así el frontend se puede desplegar sin esperar la migración
// y la migración se puede correr sin dejar una ventana rota.

function _rpcNoExiste(e) {
  const m = `${e?.message ?? ''} ${e?.code ?? ''}`;
  return /42883|PGRST202|404|does not exist|Could not find the function/i.test(m);
}

// Devuelve { [empleadoId]: { tienePin, bloqueado, bloqueadoHasta } }.
// Si la migración no está, devuelve null y la UI usa lo que traiga `fromEmpleado`.
export async function getEstadoPines() {
  try {
    const { data, error } = await supabase.rpc('mezo_pin_estado', { p_business_id: bid() });
    if (error) throw error;
    const mapa = {};
    for (const f of data ?? []) {
      mapa[f.empleado_id] = {
        tienePin:       Boolean(f.tiene_pin),
        bloqueado:      Boolean(f.bloqueado),
        bloqueadoHasta: f.bloqueado_hasta ?? null,
      };
    }
    return mapa;
  } catch (e) {
    if (_rpcNoExiste(e)) return null;   // pre-0053: sin novedad
    console.warn('[mezo] no pude leer el estado de los PIN:', e?.message);
    return null;
  }
}

// Fija el PIN de un empleado. `pin` vacío o null lo BORRA — que es también el
// "desbloquear" del admin cuando alguien falló demasiadas veces.
// Devuelve { ok, tienePin }.
export async function setPinEmpleado(empleadoId, pin) {
  const limpio = String(pin ?? '').replace(/\D/g, '').slice(0, 4);
  try {
    const { data, error } = await supabase.rpc('mezo_set_pin', {
      p_empleado: empleadoId,
      p_pin:      limpio || null,
    });
    if (error) throw error;
    return { ok: Boolean(data?.ok), tienePin: Boolean(data?.tiene_pin) };
  } catch (e) {
    if (_rpcNoExiste(e)) {
      // Camino viejo: la columna en claro todavía acepta escritura.
      await restWrite('PATCH', `/team_members?id=eq.${empleadoId}&business_id=eq.${bid()}`,
        { pin: limpio || null }, 'return=minimal');
      return { ok: true, tienePin: Boolean(limpio) };
    }
    throw e;
  }
}

// ─── Checklist de apertura/cierre + Llave del local (migración 0058) ────────

// Guarda la configuración del checklist ({activo, apertura[], cierre[]}).
// Un trigger del servidor la sanea de nuevo — el saneo del cliente
// (sanearChecklistConfig) es cortesía de UX, no la defensa.
export async function guardarChecklistConfig(cfg) {
  const row = await restWrite('PATCH', `/businesses?id=eq.${bid()}`,
    { checklist_config: cfg }, 'return=representation');
  return one(row)?.checklist_config ?? cfg;
}

// Liga la venta con el cliente de fidelidad (0064). Best-effort: el cliente
// se crea DESPUÉS de cerrar la venta, así que el vínculo es una 2ª llamada.
export async function vincularClienteVenta(saleId, cliente) {
  if (!saleId || !cliente?.id) return false;
  try {
    const { error } = await supabase.rpc('mezo_vincular_cliente_venta', { p_sale_id: saleId, p_client_id: cliente.id });
    return !error;
  } catch { return false; }
}

// Guarda los medios de pago del negocio ({lista: [{id, nombre, tipo}]}).
// Mismo contrato que el checklist: el trigger del servidor sanea de nuevo.
// Se sanea aquí también (no solo en la UI): comision_pct/comision_fija
// llegan como texto mientras se editan y el trigger 0066a solo respeta los
// valores cuando son NÚMEROS jsonb — un "300" en texto se guardaría como 0.
export async function guardarMetodosPago(cfg) {
  const limpio = sanearMetodosPago(cfg);
  const row = await restWrite('PATCH', `/businesses?id=eq.${bid()}`,
    { metodos_pago: limpio }, 'return=representation');
  return one(row)?.metodos_pago ?? limpio;
}

// ─── Descuentos con autorización (0067) ─────────────────────────────────────
export async function guardarDescuentosConfig(cfg) {
  const limpio = sanearDescuentosConfig(cfg);
  const row = await restWrite('PATCH', `/businesses?id=eq.${bid()}`,
    { descuentos_config: limpio }, 'return=representation');
  return one(row)?.descuentos_config ?? limpio;
}

// Verifica el PIN de un empleado (hash en el servidor, 0053). Devuelve
// { ok, motivo: 'ok'|'incorrecto'|'bloqueado'|'sin_pin', intentosRestantes, esperaSegundos }.
// Un `ok` deja `verificado_en = now()` en el servidor: es lo que el trigger
// 0067 exige para aceptar un descuento autorizado por ese empleado.
export async function verificarPinEmpleado(empleadoId, pin) {
  const { data, error } = await supabase.rpc('mezo_verificar_pin', {
    p_empleado: empleadoId,
    p_pin:      String(pin ?? '').replace(/\D/g, '').slice(0, 4),
  });
  if (error) throw error;
  return {
    ok:                Boolean(data?.ok),
    motivo:            data?.motivo ?? (data?.ok ? 'ok' : 'incorrecto'),
    intentosRestantes: data?.intentos_restantes ?? null,
    esperaSegundos:    Number(data?.espera_segundos ?? 0) || 0,
  };
}

// ─── Propinas repartidas (0067) ─────────────────────────────────────────────
async function _rpcOrThrow(nombre, args) {
  const { data, error } = await supabase.rpc(nombre, args);
  if (error) throw new Error(error.message || `Falló ${nombre}`);
  return data;
}
// Resumen del rango (días Colombia, ISO YYYY-MM-DD): total, por_dia, por_medio,
// empleados con horas de turno, repartos_previos que se solapan.
export const getPropinasResumen = ({ desde, hasta }) =>
  _rpcOrThrow('mezo_propinas_resumen', { p_business_id: bid(), p_desde: desde, p_hasta: hasta });
// detalle: [{ empleado_id, horas, peso, monto }] — el servidor completa nombre/rol.
export const repartirPropinas = ({ desde, hasta, modo, detalle, nota }) =>
  _rpcOrThrow('mezo_propinas_repartir', { p_business_id: bid(), p_desde: desde, p_hasta: hasta, p_modo: modo, p_detalle: detalle, p_nota: nota ?? null });
export const listarRepartosPropinas = (limite = 30) =>
  _rpcOrThrow('mezo_propinas_listar', { p_business_id: bid(), p_limite: limite });
export const misPropinas = () => _rpcOrThrow('mezo_propinas_mias', { p_business_id: bid() });
export const anularRepartoPropinas = (id, motivo) =>
  _rpcOrThrow('mezo_propinas_anular', { p_id: id, p_motivo: motivo ?? null });

// Envía un checklist diligenciado. `tareas` = [{texto, hecha}] — snapshot
// completo de la lista de ese día, no solo lo marcado: si el admin edita la
// plantilla después, los registros viejos siguen diciendo qué se pidió.
export async function registrarChecklist({ turno, tareas, observaciones, empleadoId, empleadoNombre }) {
  const row = await restWrite('POST', '/checklist_registros', {
    business_id:     bid(),
    empleado_id:     empleadoId ?? null,
    empleado_nombre: empleadoNombre ?? null,
    turno,
    tareas,
    observaciones:   observaciones ?? null,
  }, 'return=representation');
  return one(row);
}

// Registros recientes (para que el admin vea quién hizo qué y cuándo).
export async function getRegistrosChecklist({ dias = 14 } = {}) {
  const desde = new Date(Date.now() - dias * 86400000).toISOString();
  try {
    const rows = await restGet(
      `/checklist_registros?business_id=eq.${bid()}&creado_en=gte.${desde}` +
      `&select=*&order=creado_en.desc&limit=120`
    );
    return (rows ?? []).map((r) => ({
      id:            r.id,
      turno:         r.turno,
      empleadoNombre: r.empleado_nombre ?? null,
      tareas:        Array.isArray(r.tareas) ? r.tareas : [],
      observaciones: r.observaciones ?? null,
      creadoEn:      r.creado_en,
    }));
  } catch { return []; }   // pre-0058: la tabla no existe y la sección sale vacía
}

// ── Llave del local ─────────────────────────────────────────────────────────

// Enciende/apaga la llave (solo admin). Encenderla ROTA el secreto.
export async function activarLlaveAsistencia(activar) {
  const { data, error } = await supabase.rpc('mezo_llave_activar', {
    p_business_id: bid(), p_activar: !!activar,
  });
  if (error) {
    if (_rpcNoExiste(error)) throw new Error('sin_migracion_0058');
    throw error;
  }
  return data;
}

// El código vigente. SOLO responde a un admin — esa restricción vive en el
// servidor y es la garantía entera: el código se ve en la caja del local
// (sesión admin) y en ningún otro lado.
export async function getLlaveActual() {
  try {
    const { data, error } = await supabase.rpc('mezo_llave_actual', { p_business_id: bid() });
    if (error) throw error;
    if (data?.ok === false) return null;
    return { codigo: data.codigo, expiraEnSeg: Number(data.expira_en_seg) || 45 };
  } catch { return null; }
}

// Canjea el código por un pase de entrada (3 min). Pre-0058 la RPC no existe
// y el trigger tampoco: se responde ok para que marcar nunca se bloquee por
// una migración que aún no corre.
export async function canjearLlaveAsistencia(codigo) {
  try {
    const { data, error } = await supabase.rpc('mezo_llave_canjear', {
      p_business_id: bid(), p_codigo: String(codigo ?? '').trim(),
    });
    if (error) throw error;
    return { ok: Boolean(data?.ok), motivo: data?.motivo ?? null };
  } catch (e) {
    if (_rpcNoExiste(e)) return { ok: true, sinMigracion: true };
    throw e;
  }
}

// ─── Control de horas (migración 0014) ──────────────────────────────────────

// Escritura POR CAPAS (misma idea que _guardarRecetaProducto): intenta el
// cuerpo completo y, si PostgREST responde 400 por una columna que aún no
// existe (migración sin correr), reintenta con la capa siguiente. Los intentos
// intermedios son SILENCIOSOS (sin el toast de restWrite: "column … does not
// exist" no le sirve al empleado); solo el último usa restWrite y sí avisa.
async function _escribirPorCapas(method, path, capas) {
  for (let i = 0; i < capas.length; i++) {
    const esUltima = i === capas.length - 1;
    if (esUltima) return one(await restWrite(method, path, capas[i]));
    try {
      const headers = { ...(await authHeaders()), Prefer: 'return=representation' };
      const res = await fetch(`${SUPABASE_URL}/rest/v1${path}`, {
        method, headers, body: JSON.stringify(capas[i]),
      });
      const text = await res.text();
      let json; try { json = text ? JSON.parse(text) : null; } catch { json = null; }
      if (res.ok) return one(json);
      // eslint-disable-next-line no-console
      console.warn('[mezo] turno: reintento sin columnas de evidencia (¿falta la migración 0050?):', json?.message || res.status);
    } catch (e) {
      if (e?.message === 'no_session') throw e;
      // eslint-disable-next-line no-console
      console.warn('[mezo] turno: reintento sin columnas de evidencia:', e?.message);
    }
  }
  return null;
}

// Número finito o null. Number(null) es 0: sin esto una marcación sin GPS
// guardaría lat/lng = 0 (Golfo de Guinea) en vez de "sin ubicación".
const _num = (v) => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Columnas de evidencia (0050) para una marcación. `lado` = 'entrada' | 'salida'.
// Solo incluye lo que venga: un PATCH con lat null pisaría una lat real.
function _columnasEvidencia(lado, ev = {}) {
  const c = {};
  const lat = _num(ev.lat), lng = _num(ev.lng);
  const precision = _num(ev.precisionM), distancia = _num(ev.distanciaM);
  if (ev.fotoPath)                     c[`foto_${lado}_path`]   = ev.fotoPath;
  if (lat !== null && lng !== null) {
    c[`lat_${lado}`] = lat;
    c[`lng_${lado}`] = lng;
  }
  if (precision !== null)              c[`precision_${lado}_m`] = Math.round(precision);
  if (distancia !== null)              c[`distancia_${lado}_m`] = Math.round(distancia);
  if (typeof ev.enPunto === 'boolean') c[`en_punto_${lado}`]    = ev.enPunto;
  if (ev.dispositivo)                  c.dispositivo            = String(ev.dispositivo).slice(0, 120);
  return c;
}

// Marcar entrada de un empleado específico (lo usa el admin desde /empleados,
// o el propio empleado desde /mi-turno). Si ya tiene un turno abierto,
// devuelve ese turno (no crea duplicado).
//
// Evidencia (0050, opcional): { fotoPath, lat, lng, precisionM, distanciaM,
// enPunto, dispositivo, nota }. Best-effort: si las columnas no existen, el
// turno se crea igual sin evidencia.
export async function iniciarTurnoEmpleado({
  empleadoId, empleadoNombre, roles, baseTurno = 0,
  fotoPath, lat, lng, precisionM, distanciaM, enPunto, dispositivo, nota,
} = {}) {
  // ¿Ya tiene turno abierto?
  const abierto = one(await restGet(
    `/shifts?business_id=eq.${bid()}&empleado_id=eq.${empleadoId}&fin=is.null&select=*&limit=1`
  ));
  if (abierto) return fromShift(abierto);

  const base = {
    business_id:     bid(),
    empleado_id:     empleadoId,
    empleado_nombre: empleadoNombre ?? null,
    roles:           roles ?? ['cajera'],
    base_turno:      Number(baseTurno) || 0,
  };
  if (nota && String(nota).trim()) base.nota = `Entrada: ${String(nota).trim()}`;   // columna de 0014
  const evidencia = _columnasEvidencia('entrada', { fotoPath, lat, lng, precisionM, distanciaM, enPunto, dispositivo });
  const capas = Object.keys(evidencia).length ? [{ ...base, ...evidencia }, base] : [base];
  const row = await _escribirPorCapas('POST', '/shifts', capas);
  return fromShift(row);
}

// Cierra el turno abierto del empleado (calcula duracion_min automático).
// Segundo argumento (0050, opcional): evidencia de SALIDA con la misma forma
// que en iniciarTurnoEmpleado.
export async function finalizarTurnoEmpleado(empleadoId, {
  fotoPath, lat, lng, precisionM, distanciaM, enPunto, dispositivo, nota,
} = {}) {
  const abierto = one(await restGet(
    `/shifts?business_id=eq.${bid()}&empleado_id=eq.${empleadoId}&fin=is.null&select=*&limit=1`
  ));
  if (!abierto) return null;

  const ahora = new Date();
  const duracionMin = Math.round((ahora.getTime() - new Date(abierto.inicio).getTime()) / 60000);

  const base = { fin: ahora.toISOString(), duracion_min: duracionMin };
  if (nota && String(nota).trim()) {
    // Conserva la nota de entrada si la hubo: "Entrada: … · Salida: …".
    base.nota = [abierto.nota, `Salida: ${String(nota).trim()}`].filter(Boolean).join(' · ');
  }
  const evidencia = _columnasEvidencia('salida', { fotoPath, lat, lng, precisionM, distanciaM, enPunto, dispositivo });
  const capas = Object.keys(evidencia).length ? [{ ...base, ...evidencia }, base] : [base];
  const row = await _escribirPorCapas('PATCH',
    `/shifts?id=eq.${abierto.id}&business_id=eq.${bid()}`, capas);
  return fromShift(row);
}

// Lista los turnos del mes (cerrados + abiertos) para el reporte. Si
// `empleadoId` se pasa, filtra por ese empleado.
export async function listarTurnosMes({ ano, mes, empleadoId = null } = {}) {
  const y = ano ?? new Date().getFullYear();
  const m = mes ?? new Date().getMonth(); // 0-indexed
  const inicioMes = new Date(y, m, 1).toISOString();
  const finMes    = new Date(y, m + 1, 1).toISOString();
  let path = `/shifts?business_id=eq.${bid()}&inicio=gte.${inicioMes}&inicio=lt.${finMes}&select=*&order=inicio.desc`;
  if (empleadoId) path += `&empleado_id=eq.${empleadoId}`;
  try {
    const rows = await restGet(path);
    return (rows ?? []).map(fromShift);
  } catch { return []; }
}

// ─── Asistencia con evidencia: foto + GPS (migración 0050) ──────────────────
// Pedido del cliente piloto: "que en el celular se pueda tomar una foto o
// pedir alguna evidencia de que llegaron y si están en el punto por GPS".

// Últimos N turnos de un empleado sin importar el mes (historial de /mi-turno:
// a primero de mes listarTurnosMes saldría vacío).
export async function listarUltimosTurnos({ empleadoId, limite = 7 } = {}) {
  if (!empleadoId) return [];
  try {
    const rows = await restGet(
      `/shifts?business_id=eq.${bid()}&empleado_id=eq.${empleadoId}&select=*&order=inicio.desc&limit=${Number(limite) || 7}`
    );
    return (rows ?? []).map(fromShift);
  } catch { return []; }
}

// El team_member del usuario logueado en el negocio actual (para que cada
// quien marque SU turno). Busca por user_id y, como resolveBusiness, cae a
// email para cuentas invitadas que aún no tienen user_id reparado. null =
// dueño viejo sin fila propia (la UI muestra el aviso y manda a Empleados).
export async function getMiEmpleado() {
  const { data: u } = await supabase.auth.getUser();
  if (!u?.user) return null;
  const id = getBusinessId();
  if (!id) return null;
  try {
    let row = one(await restGet(
      `/team_members?user_id=eq.${u.user.id}&business_id=eq.${id}&select=*&limit=1`
    ));
    if (!row && u.user.email) {
      row = one(await restGet(
        `/team_members?business_id=eq.${id}&email=eq.${encodeURIComponent(String(u.user.email).toLowerCase())}&select=*&limit=1`
      ));
    }
    return row ? fromEmpleado(row) : null;
  } catch { return null; }
}

// Sube la foto de una marcación al bucket PRIVADO `asistencia`.
// Path: <business_id>/<empleado_id>/<yyyy-mm>/<timestamp>-<tipo>.jpg — la
// segunda carpeta es la que autoriza al propio empleado a ver su foto (RLS).
// Devuelve el path (se guarda en shifts) o null si no se pudo subir (bucket
// inexistente pre-0050, sin red…): el turno se marca igual, sin foto.
export async function subirFotoAsistencia(blob, { empleadoId, tipo = 'entrada' } = {}) {
  if (!blob || !empleadoId) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;

  const businessId = bid();
  const ahora = new Date();
  const mes = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;
  const lado = tipo === 'salida' ? 'salida' : 'entrada';
  const path = `${businessId}/${empleadoId}/${mes}/${ahora.getTime()}-${lado}.jpg`;

  try {
    const res = await fetch(
      `${SUPABASE_URL}/storage/v1/object/asistencia/${path}`,
      {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          'Content-Type': blob.type || 'image/jpeg',
          // Sin x-upsert: la evidencia es inmutable y no hay policy de UPDATE.
        },
        body: blob,
      },
    );
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn('[mezo] foto de asistencia no subida (¿falta la migración 0050 / bucket `asistencia`?):', await res.text().catch(() => res.status));
      return null;
    }
    return path;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[mezo] foto de asistencia no subida:', e?.message);
    return null;
  }
}

// Signed URL (1 h por defecto) para ver una foto de asistencia. Solo la
// obtienen el admin del negocio o el propio empleado (policy de 0050).
export async function urlFotoAsistencia(path, segundosValidez = 3600) {
  if (!path) return null;
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;
  try {
    const res = await fetch(
      `${SUPABASE_URL}/storage/v1/object/sign/asistencia/${path}`,
      {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiresIn: Number(segundosValidez) || 3600 }),
      },
    );
    if (res.ok) {
      const data = await res.json();
      if (data?.signedURL) return `${SUPABASE_URL}/storage/v1${data.signedURL}`;
    }
  } catch { /* cae al cliente oficial */ }
  try {
    const { data } = await supabase.storage.from('asistencia').createSignedUrl(path, Number(segundosValidez) || 3600);
    return data?.signedUrl ?? null;
  } catch { return null; }
}

// Forma canónica de la config de asistencia del negocio (lo que lee /mi-turno
// y edita Configuración → Asistencia).
const UBICACION_DEFAULT = { lat: null, lng: null, radioM: 150, requiereFoto: false, requiereGps: false, persistido: false };

export async function getUbicacionNegocio() {
  try {
    const row = one(await restGet(
      `/businesses?id=eq.${bid()}&select=ubicacion_lat,ubicacion_lng,radio_asistencia_m,asistencia_requiere_foto,asistencia_requiere_gps&limit=1`
    ));
    if (!row) return { ...UBICACION_DEFAULT };
    return {
      lat:          row.ubicacion_lat ?? null,
      lng:          row.ubicacion_lng ?? null,
      radioM:       Number(row.radio_asistencia_m ?? 150) || 150,
      requiereFoto: !!row.asistencia_requiere_foto,
      requiereGps:  !!row.asistencia_requiere_gps,
      persistido:   true,
    };
  } catch {
    // Columnas inexistentes (pre-0050) o sin red: la app sigue con defaults
    // → el botón marca directo, como hasta hoy.
    return { ...UBICACION_DEFAULT };
  }
}

// Guarda punto + radio + reglas. PATCH silencioso: si las columnas no existen
// lanza Error('sin_migracion_0050') para que la UI lo diga claro (best-effort
// no significa fingir que se guardó).
export async function guardarUbicacionNegocio({ lat, lng, radioM, requiereFoto, requiereGps } = {}) {
  const patch = {};
  if (lat !== undefined)          patch.ubicacion_lat            = _num(lat);
  if (lng !== undefined)          patch.ubicacion_lng            = _num(lng);
  if (radioM !== undefined)       patch.radio_asistencia_m       = Math.min(5000, Math.max(10, Math.round(Number(radioM) || 150)));
  if (requiereFoto !== undefined) patch.asistencia_requiere_foto = !!requiereFoto;
  if (requiereGps !== undefined)  patch.asistencia_requiere_gps  = !!requiereGps;
  if (!Object.keys(patch).length) return getUbicacionNegocio();

  const headers = { ...(await authHeaders()), Prefer: 'return=representation' };
  const res = await fetch(`${SUPABASE_URL}/rest/v1/businesses?id=eq.${bid()}`, {
    method: 'PATCH', headers, body: JSON.stringify(patch),
  });
  const text = await res.text();
  let json; try { json = text ? JSON.parse(text) : null; } catch { json = null; }
  if (!res.ok) {
    const msg = json?.message || `Error ${res.status}`;
    // eslint-disable-next-line no-console
    console.warn('[mezo] asistencia no guardada (¿falta la migración 0050?):', msg);
    throw new Error(/column|schema cache|does not exist/i.test(msg) ? 'sin_migracion_0050' : msg);
  }
  const row = one(json) ?? {};
  return {
    lat:          row.ubicacion_lat ?? null,
    lng:          row.ubicacion_lng ?? null,
    radioM:       Number(row.radio_asistencia_m ?? 150) || 150,
    requiereFoto: !!row.asistencia_requiere_foto,
    requiereGps:  !!row.asistencia_requiere_gps,
    persistido:   true,
  };
}

// ─── Ventas (tabla sales) ────────────────────────────────────────────────────
// `sales.total` es LO QUE ENTRÓ A LA CAJA: subtotal + propina − canje de
// fidelidad (lo congela el servidor en mezo_cerrar_venta). `orders.total`
// en cambio es solo el subtotal de las líneas — sumarlo para arqueo produce
// "sobrantes" iguales a las propinas y "faltantes" iguales a los canjes.

// El enum de la DB guarda BOLD para datáfono; el frontend usa CARD.
const PM_DB_TO_FRONT = { BOLD: 'CARD' };

function fromSale(s) {
  const total = Number(s.total) || 0;
  const tip   = Number(s.tip)   || 0;
  return {
    id:            s.id,
    orderId:       s.order_id,
    paymentMethod: PM_DB_TO_FRONT[s.payment_method] ?? s.payment_method,
    total,               // plata real que entró a la caja
    tip,                 // propina incluida dentro de `total`
    neto: total - tip,   // venta sin propina (subtotal − canje)
    comentario: s.comentario ?? null,   // sugerencia del cliente al pagar (0059)
    metodoNombre: s.metodo_nombre ?? null, // nombre custom del medio de pago (0060)
    // Cliente de fidelidad ligado a la venta (0064). null = venta anónima.
    cliente: s.loyalty_clients ? { id: s.loyalty_clients.id, nombre: s.loyalty_clients.nombre ?? null, celular: s.loyalty_clients.celular ?? null } : null,
    // Pagos mixtos (0061): partes de la venta. Vacío = un solo medio (lo de siempre).
    pagos: (s.sale_payments ?? []).map((p) => ({
      tipo: p.payment_method, metodo: PM_DB_TO_FRONT[p.payment_method] ?? p.payment_method,
      nombre: p.metodo_nombre ?? null, monto: Number(p.monto) || 0,
    })),
    createdAt: s.created_at ? new Date(s.created_at) : new Date(),
  };
}

// Ventas cerradas del rango. Fechas por created_at de la VENTA (momento del
// cobro), no de la orden — una mesa abierta anoche y cobrada hoy cuenta hoy.
export async function getVentas(params = {}) {
  let path = `/sales?business_id=eq.${bid()}` +
    `&select=id,order_id,payment_method,total,tip,comentario,metodo_nombre,created_at,sale_payments(payment_method,metodo_nombre,monto),loyalty_clients(id,nombre,celular)&order=created_at.desc`;
  if (params.desde) path += `&created_at=gte.${new Date(params.desde).toISOString()}`;
  if (params.hasta) {
    const h = new Date(params.hasta);
    h.setHours(23, 59, 59, 999);
    path += `&created_at=lte.${h.toISOString()}`;
  }
  const rows = await restGet(path);
  return (rows ?? []).map(fromSale);
}

// ─── Reportes ────────────────────────────────────────────────────────────────
// Convención de plata en reportes: "ventas" = neto sin propinas (las propinas
// son del equipo, no ingreso del negocio) y con canjes ya descontados.

export async function getKPIs(desde, hasta) {
  const [ventas, ordenes] = await Promise.all([
    getVentas({ desde, hasta }),
    getOrdenes({ desde, hasta }),
  ]);
  const totalVentas = ventas.reduce((s, v) => s + v.neto, 0);
  const numOrdenes  = ventas.length;

  const horas = ventas.map((v) => new Date(v.createdAt).getHours());
  const contHoras = horas.reduce((m, h) => { m[h] = (m[h] ?? 0) + 1; return m; }, {});
  const mejorHora = Object.entries(contHoras).sort((a, b) => b[1] - a[1])[0]?.[0];

  const metodoCount = {};
  for (const v of ventas) {
    if (v.paymentMethod) metodoCount[v.paymentMethod] = (metodoCount[v.paymentMethod] ?? 0) + 1;
  }

  // El producto top sí necesita las líneas — sale de las órdenes pagadas.
  const prodCount = {};
  for (const o of ordenes.filter((o) => o.status === 'PAID')) {
    for (const l of o.items ?? []) {
      prodCount[l.name] = (prodCount[l.name] ?? 0) + (l.quantity ?? 0);
    }
  }
  const productoTop = Object.entries(prodCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  const metodoTop   = Object.entries(metodoCount).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    totalVentas,
    numOrdenes,
    ticketProm: numOrdenes ? Math.round(totalVentas / numOrdenes) : 0,
    mejorHora:  mejorHora != null ? Number(mejorHora) : null,
    productoTop,
    metodoTop,
  };
}

export async function getVentasPorDia(desde, hasta) {
  const ventas = await getVentas({ desde, hasta });
  const porDia = {};
  ventas.forEach((v) => {
    const d = new Date(v.createdAt);
    const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    porDia[k] = (porDia[k] ?? 0) + v.neto;
  });
  return Object.entries(porDia)
    .map(([fecha, total]) => ({ fecha, total }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

// ─── Lealtad / Fidelidad ─────────────────────────────────────────────────────

function fromCliente(c) {
  if (!c) return null;
  return {
    id:           c.id,
    celular:      c.celular,
    nombre:       c.nombre ?? null,
    visitas:      c.visitas ?? 0,
    puntos:       Number(c.puntos ?? 0),
    ultimaVisita: c.ultima_visita ? new Date(c.ultima_visita) : null,
    cumple:       c.cumple ?? null,
    notas:        c.notas ?? null,
    descuentoMonto:  Number(c.descuento_monto) || 0,
    descuentoMotivo: c.descuento_motivo ?? null,
  };
}

export async function getCliente(celular) {
  if (!celular) return null;
  try {
    return fromCliente(one(await restGet(
      `/loyalty_clients?business_id=eq.${bid()}&celular=eq.${celular}&select=*&limit=1`
    )));
  } catch {
    return null;
  }
}

// Busca clientes por celular O nombre — usado en el autocompletar al cobrar.
// Para que el cajero pueda escribir "Maria" o "3001234" y encontrar al cliente.
export async function buscarClientes(query, limite = 5) {
  if (!query?.trim()) return [];
  const q = query.trim();
  const businessId = bid();
  try {
    // PostgREST: or= permite combinar filtros. ilike es case-insensitive con %
    // Si parece número, buscamos también por celular que empieza con eso
    const esDigito = /^\d+$/.test(q);
    const filtro = esDigito
      ? `or=(celular.ilike.${encodeURIComponent(q + '%')},nombre.ilike.${encodeURIComponent('%' + q + '%')})`
      : `nombre=ilike.${encodeURIComponent('%' + q + '%')}`;
    const rows = await restGet(
      `/loyalty_clients?business_id=eq.${businessId}&${filtro}&select=*&order=puntos.desc&limit=${limite}`
    );
    return (rows ?? []).map(fromCliente);
  } catch { return []; }
}

// Lista los top clientes del negocio para la página /clientes.
// Por defecto ordena por puntos (los más valiosos primero).
export async function listarClientesFrecuentes({ limite = 100, orderBy = 'puntos' } = {}) {
  try {
    const orden = orderBy === 'visitas' ? 'visitas.desc' :
                  orderBy === 'reciente' ? 'ultima_visita.desc' :
                                           'puntos.desc';
    const rows = await restGet(
      `/loyalty_clients?business_id=eq.${bid()}&select=*&order=${orden}&limit=${limite}`
    );
    return (rows ?? []).map(fromCliente);
  } catch { return []; }
}

// Registra visita + suma puntos basados en el monto de la venta y la
// configuración del negocio (loyalty_puntos_por_cop). Si el cliente no
// existe, lo crea. Devuelve el cliente actualizado.
//
// Cálculo: puntos = floor(monto_cop / 1000) * loyalty_puntos_por_cop
// Default 1.0 → 1 punto por cada $1.000 gastado.
export async function registrarVisita(celular, montoVenta = 0, datosExtra = {}) {
  if (!celular) return null;
  try {
    // Leer config del negocio para saber tasa de puntos
    const negocio = one(await restGet(
      `/businesses?id=eq.${bid()}&select=loyalty_puntos_por_cop&limit=1`
    ));
    const tasa = Number(negocio?.loyalty_puntos_por_cop ?? 1);
    const puntosGanados = Math.floor((Number(montoVenta) || 0) / 1000) * tasa;

    const ex = one(await restGet(
      `/loyalty_clients?business_id=eq.${bid()}&celular=eq.${celular}&select=*&limit=1`
    ));

    let data;
    if (!ex) {
      data = one(await restWrite('POST', '/loyalty_clients', {
        business_id:   bid(),
        celular,
        visitas:       1,
        puntos:        puntosGanados,
        nombre:        datosExtra.nombre ?? null,
        cumple:        datosExtra.cumple ?? null,
        ultima_visita: new Date().toISOString(),
      }));
    } else {
      data = one(await restWrite('PATCH',
        `/loyalty_clients?id=eq.${ex.id}&business_id=eq.${bid()}`, {
        visitas:       (ex.visitas ?? 0) + 1,
        puntos:        (Number(ex.puntos) || 0) + puntosGanados,
        // Solo actualizar nombre/cumple si vienen explícitos (no pisar)
        ...(datosExtra.nombre ? { nombre: datosExtra.nombre } : {}),
        ...(datosExtra.cumple ? { cumple: datosExtra.cumple } : {}),
        ultima_visita: new Date().toISOString(),
      }));
    }
    return fromCliente(data);
  } catch { return null; }
}

// ─── Recompensas configurables ──────────────────────────────────────────────

function fromReward(r) {
  if (!r) return null;
  return {
    id:               r.id,
    nombre:           r.nombre,
    descripcion:      r.descripcion ?? null,
    puntosRequeridos: r.puntos_requeridos,
    tipo:             r.tipo,
    productoId:       r.producto_id ?? null,
    montoCop:         r.monto_cop != null ? Number(r.monto_cop) : null,
    porcentaje:       r.porcentaje ?? null,
    activo:           r.activo ?? true,
    creadoEn:         r.created_at ? new Date(r.created_at) : null,
  };
}

export async function getRecompensas() {
  try {
    const rows = await restGet(
      `/loyalty_rewards?business_id=eq.${bid()}&select=*&order=puntos_requeridos.asc`
    );
    return (rows ?? []).map(fromReward);
  } catch { return []; }
}

export async function crearRecompensa(payload) {
  const row = {
    business_id:        bid(),
    nombre:             payload.nombre,
    descripcion:        payload.descripcion ?? null,
    puntos_requeridos:  Number(payload.puntosRequeridos) || 1,
    tipo:               payload.tipo ?? 'producto',
    producto_id:        payload.productoId ?? null,
    monto_cop:          payload.montoCop ?? null,
    porcentaje:         payload.porcentaje ?? null,
    activo:             payload.activo ?? true,
  };
  return fromReward(one(await restWrite('POST', '/loyalty_rewards', row)));
}

export async function actualizarRecompensa(id, payload) {
  const update = {};
  if (payload.nombre !== undefined)           update.nombre = payload.nombre;
  if (payload.descripcion !== undefined)      update.descripcion = payload.descripcion;
  if (payload.puntosRequeridos !== undefined) update.puntos_requeridos = Number(payload.puntosRequeridos);
  if (payload.tipo !== undefined)             update.tipo = payload.tipo;
  if (payload.productoId !== undefined)       update.producto_id = payload.productoId;
  if (payload.montoCop !== undefined)         update.monto_cop = payload.montoCop;
  if (payload.porcentaje !== undefined)       update.porcentaje = payload.porcentaje;
  if (payload.activo !== undefined)           update.activo = payload.activo;
  return fromReward(one(await restWrite('PATCH',
    `/loyalty_rewards?id=eq.${id}&business_id=eq.${bid()}`, update)));
}

export async function eliminarRecompensa(id) {
  await restWrite('DELETE',
    `/loyalty_rewards?id=eq.${id}&business_id=eq.${bid()}`, null, 'return=minimal');
  return { ok: true };
}

// Canjea una recompensa: resta puntos del cliente y registra en historial.
// Opcionalmente asocia la venta (sale_id) para auditoría.
export async function canjearRecompensa({ clienteId, rewardId, saleId = null }) {
  const businessId = bid();
  const reward = one(await restGet(
    `/loyalty_rewards?id=eq.${rewardId}&business_id=eq.${businessId}&select=*&limit=1`
  ));
  if (!reward) throw new Error('reward_no_existe');

  const cliente = one(await restGet(
    `/loyalty_clients?id=eq.${clienteId}&business_id=eq.${businessId}&select=*&limit=1`
  ));
  if (!cliente) throw new Error('cliente_no_existe');

  const puntosActuales = Number(cliente.puntos ?? 0);
  if (puntosActuales < reward.puntos_requeridos) {
    throw new Error('puntos_insuficientes');
  }

  // Restar puntos
  await restWrite('PATCH',
    `/loyalty_clients?id=eq.${clienteId}&business_id=eq.${businessId}`,
    { puntos: puntosActuales - reward.puntos_requeridos }, 'return=minimal');

  // Registrar en historial
  const canje = one(await restWrite('POST', '/loyalty_canjes', {
    business_id:    businessId,
    cliente_id:     clienteId,
    reward_id:      rewardId,
    reward_nombre:  reward.nombre,
    puntos_usados:  reward.puntos_requeridos,
    sale_id:        saleId,
  }));

  return { ok: true, canje, puntosRestantes: puntosActuales - reward.puntos_requeridos };
}

// Historial de canjes de un cliente para mostrar en su detalle.
export async function historialCanjes(clienteId) {
  try {
    return await restGet(
      `/loyalty_canjes?cliente_id=eq.${clienteId}&business_id=eq.${bid()}&select=*&order=created_at.desc`
    );
  } catch { return []; }
}

// ─── Estado del día (Empezar día / Cerrar día) ───────────────────────────────

export async function getDiaActivo() {
  try {
    const data = one(await restGet(
      `/business_days?business_id=eq.${bid()}&closed_at=is.null&select=*&order=opened_at.desc&limit=1`
    ));
    if (!data) return { diaAbierto: false, abiertaAt: null, diaId: null, base: 0 };
    return { diaAbierto: true, abiertaAt: data.opened_at, diaId: data.id, base: data.base ?? 0 };
  } catch {
    return { diaAbierto: false, abiertaAt: null, diaId: null, base: 0 };
  }
}

export async function abrirDia(base = 0) {
  const actual = await getDiaActivo();
  if (actual.diaAbierto) return actual;
  const baseNum = Math.max(0, Math.round(Number(base) || 0));
  let data;
  try {
    data = one(await restWrite('POST', '/business_days', { business_id: bid(), base: baseNum }));
  } catch (e) {
    // Migración 0048 sin correr → columna base no existe: abrir sin base
    // (el día JAMÁS puede dejar de abrirse por esto).
    data = one(await restWrite('POST', '/business_days', { business_id: bid() }));
  }
  return { diaAbierto: true, abiertaAt: data.opened_at, diaId: data.id, base: data.base ?? baseNum };
}

export async function cerrarDia(arqueo = null) {
  // Antes esta función se tragaba su propio fallo: el `.catch(() => [])` hacía
  // que un error de red o de permisos se viera igual que "no hay día abierto",
  // y en los dos casos devolvía un objeto TRUTHY. La pantalla de Arqueo lo leía
  // como éxito: decía "Día cerrado y arqueo guardado en histórico ✓", borraba el
  // conteo que el cajero acababa de digitar… y nada se había guardado. Volver a
  // contar la caja a las once de la noche por un mensaje optimista no tiene
  // perdón, así que ahora el error SUBE y "no había día abierto" devuelve null.
  const activo = one(await restGet(
    `/business_days?business_id=eq.${bid()}&closed_at=is.null&select=id&order=opened_at.desc&limit=1`
  ));
  if (!activo) return null;

  // El objeto de éxito se devuelve SOLO después de que el PATCH resuelva.
  await restWrite('PATCH', `/business_days?id=eq.${activo.id}`,
    { closed_at: new Date().toISOString(), arqueo }, 'return=minimal');
  return { diaAbierto: false, abiertaAt: null, diaId: null };
}

export async function getHistoricoArqueos() {
  try {
    const rows = await restGet(
      `/business_days?business_id=eq.${bid()}&closed_at=not.is.null&select=*&order=closed_at.desc`
    );
    return (rows ?? [])
      .filter((d) => d.arqueo)
      .map((d) => ({ ...d.arqueo, fecha: d.arqueo.fecha ?? d.closed_at, diaId: d.id }));
  } catch {
    return [];
  }
}
